import React, { useEffect, useState, useMemo, useRef } from 'react';
import { useSearchParams, useLocation, Link } from 'react-router-dom';
import { Copy, Trash2, Plus, Edit, Archive, HardDrive, Instagram, PlusCircle, CheckCircle2, AlertCircle, Loader, Download, X, Info, Search, Check, GitMerge, ExternalLink } from 'lucide-react';
import { isSupabaseStorageUrl, buildForcedDownloadUrl } from '../utils/video/player';
import { useStore } from '../store';
import { EditAdCreativeModal } from '../components/EditAdCreativeModal';
import { AdCreativeSubmissionModal } from '../components/AdCreativeSubmissionModal';
import { BulkAdCreativeReleaseModal } from '../components/BulkAdCreativeReleaseModal';
import { AdCreativesFilterMenu } from '../components/AdCreativesFilterMenu';
import { ConfirmationModal } from '../components/ConfirmationModal';
import { VideoPreviewModal } from '../components/VideoPreviewModal';
import { EditCampaignReleaseModal } from '../components/EditCampaignReleaseModal';
import { LinkReleaseCampaignModal, type CampaignPlatform } from '../components/LinkReleaseCampaignModal';
import { LinkReleaseSmartlinkModal } from '../components/LinkReleaseSmartlinkModal';
import { LinkReleaseReportModal } from '../components/LinkReleaseReportModal';
import { ConfirmSmartlinkModal } from '../components/ConfirmSmartlinkModal';
import { Pagination } from '../components/Pagination';
import { format } from 'date-fns';
import JSZip from 'jszip';
import { generateThumbnailFromUrl } from '../utils/video/generateThumbnailFromUrl';
import { generateBulkThumbnails, getVideosMissingThumbnails, type BulkThumbnailProgress } from '../utils/video/bulkThumbnailGenerator';
import { scanAllInstagramAudio, scanInstagramAudio } from '../utils/scanInstagramAudio';
import { fetchInstagramThumbnail } from '../utils/fetchInstagramThumbnail';
import { detectDuplicateCreatives, mergeCreatives, debugCompareFirstInstagramReel } from '../utils/compareThumbnails';
import { supabase } from '../lib/supabase';
import { logAction } from '../lib/actionLog';
import { getTikTokAuthCode, isTikTokEligible, isMetaEligible } from '../utils/adCreativeEligibility';
import { reconcileTikTokCampaign } from '../utils/tiktokReconcile';
import { applyAdCreativeFilters } from '../utils/adCreativeFilters';
import { BulkAddPlatformModal } from '../components/BulkAddPlatformModal';
import { ReleaseAssetsModal } from '../components/ReleaseAssetsModal';
import { RejectionReasonTag } from '../components/RejectionReasonTag';
import { cache } from '../utils/cache';
import toast from 'react-hot-toast';

// Reporting Dashboard Supabase client for campaign search
const REPORTING_SUPABASE_URL = 'https://uydhsjvwrgupgfjevqsz.supabase.co';
const REPORTING_SUPABASE_ANON_KEY = 'sb_publishable_BrCqK-o8PBNyoNFYpxWoxg_qDwheRR5';

interface AdCreativesProps {
  artistId?: string;
}

export function AdCreatives({ artistId }: AdCreativesProps) {
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();
  const [showSubmissionModal, setShowSubmissionModal] = useState(false);
  const [modalInitialized, setModalInitialized] = useState(false);
  const [editingCreative, setEditingCreative] = useState<any>(null);
  
  // Parse URL params manually to ensure they're read correctly.
  // IMPORTANT: also read `window.location.search` because on the very first
  // render `location.search` from react-router can briefly be an empty
  // string before the route is matched, which would cause an unfiltered
  // fetch to fire and (thanks to network races) overwrite the filtered
  // result that lands a moment later.
  const urlParams = useMemo(() => {
    const search =
      location.search ||
      (typeof window !== 'undefined' ? window.location.search : '');
    const params = new URLSearchParams(search);
    return {
      artist: params.get('artist'),
      platform: params.get('platform'),
      status: params.get('status')
    };
  }, [location.search]);
  
  // Seed from the URL synchronously on the first render (not via an effect)
  // so the very first fetch already carries the filters and we never fire an
  // unfiltered request that a later filtered one has to race against.
  const [selectedPlatform, setSelectedPlatform] = useState(() => urlParams.platform || '');
  const [selectedStatus, setSelectedStatus] = useState(() => urlParams.status || '');
  const [isInitialized, setIsInitialized] = useState(false);
  
  const {
    artists,
    fetchArtists,
    fetchAdCreatives,
    adCreativesPagination,
    setAdCreativesPage,
    loading,
    selectedArtistId,
    setSelectedArtistId,
    adCreatives,
    deleteAdCreative,
    updateAdCreativeStatus,
    archiveAdCreative
  } = useStore();
  
  // URL is the source of truth for filters. This keeps browser back/forward,
  // pasted links and direct navigation from racing with local state updates.
  useEffect(() => {
    const nextArtist = urlParams.artist || '';
    if (nextArtist !== (selectedArtistId || '')) {
      setSelectedArtistId(nextArtist || null);
    }
    if ((urlParams.platform || '') !== selectedPlatform) {
      setSelectedPlatform(urlParams.platform || '');
    }
    if ((urlParams.status || '') !== selectedStatus) {
      setSelectedStatus(urlParams.status || '');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [urlParams.artist, urlParams.platform, urlParams.status]);

  const updateFilterUrl = (key: string, value: string) => {
    const next = new URLSearchParams(window.location.search);
    if (value) next.set(key, value);
    else next.delete(key);
    setSearchParams(next, { replace: true });
  };
  const [previewModal, setPreviewModal] = useState<{
    isOpen: boolean;
    videoUrl: string;
    videoName?: string;
    platform: string;
    creativeId?: string;
  }>({
    isOpen: false,
    videoUrl: '',
    videoName: '',
    platform: '',
    creativeId: ''
  });
  const [deleteConfirmation, setDeleteConfirmation] = useState<{
    isOpen: boolean;
    creativeId: string | null;
  }>({
    isOpen: false,
    creativeId: null,
  });
  const [selectedCreatives, setSelectedCreatives] = useState<Set<string>>(new Set());
  const [hoveredMessage, setHoveredMessage] = useState<string | null>(null);
  const [duplicateMatches, setDuplicateMatches] = useState<any[]>([]);
  const [isDetectingDuplicates, setIsDetectingDuplicates] = useState(false);
  const [showDuplicateModal, setShowDuplicateModal] = useState(false);
  const [addingToCampaign, setAddingToCampaign] = useState<string | null>(null); // Track which creative is being added
  const [readdConfirmation, setReaddConfirmation] = useState<{
    isOpen: boolean;
    creative: any | null;
  }>({ isOpen: false, creative: null });
  // Release whose "Add …" button was clicked while no platform filter is
  // active — drives the platform-choice modal.
  const [bulkAddPlatformFor, setBulkAddPlatformFor] = useState<{
    releaseId: string;
    releaseName: string | null;
    metaCampaignId: string | null;
    tiktokCampaignId: string | null;
  } | null>(null);
  const [bulkAddingRelease, setBulkAddingRelease] = useState<string | null>(null); // Track which release is being bulk added
  const [bulkAddAbortController, setBulkAddAbortController] = useState<AbortController | null>(null); // Controller to cancel bulk add
  // Separate state for TikTok bulk-add so Meta and TikTok bulk operations
  // can run independently per release without clobbering each other.
  const [bulkAddingTiktokRelease, setBulkAddingTiktokRelease] = useState<string | null>(null);
  const [bulkThumbnailProgress, setBulkThumbnailProgress] = useState<BulkThumbnailProgress | null>(null);
  const [bulkThumbnailAbortController, setBulkThumbnailAbortController] = useState<AbortController | null>(null);
  const [showThumbnailModal, setShowThumbnailModal] = useState(false);
  const [videosMissingThumbnails, setVideosMissingThumbnails] = useState(0);
  const [editingCampaignRelease, setEditingCampaignRelease] = useState<{
    releaseId: string;
    releaseName: string | null;
    metaCampaignId: string | null;
    metaCampaignName: string | null;
  } | null>(null);
  // Platform-aware link/unlink modal (replaces the legacy Meta-only edit
  // campaign modal when a user clicks the Meta or TikTok pill on a release
  // divider). Supports searching campaigns, viewing the linked one, and
  // unlinking.
  const [linkingCampaign, setLinkingCampaign] = useState<{
    platform: CampaignPlatform;
    releaseId: string;
    releaseName: string | null;
    currentCampaignId: string | null;
    currentCampaignName: string | null;
  } | null>(null);
  // Smartlink (song.so) link modal state — opened from the divider's
  // "song.so" pill button beneath the Status column.
  // Release cover/Spotify-URI quick modal (opened from release group headers)
  const [assetRelease, setAssetRelease] = useState<any | null>(null);
  const [linkingSmartlink, setLinkingSmartlink] = useState<{
    releaseId: string;
    releaseName: string | null;
    artistName: string | null;
    currentSmartlinkUrl: string | null;
    currentSmartlinkId: string | null;
  } | null>(null);
  // Report link modal state — opened from the divider's "Report" pill
  // button beneath the Submitted column.
  const [linkingReport, setLinkingReport] = useState<{
    releaseId: string;
    releaseName: string | null;
    artistName: string | null;
    artistId: string | null;
    currentReportId: string | null;
  } | null>(null);
  // 409 confirmation modal: opened when add-creative-to-meta-campaign
  // returns a song.so suggestion that needs human verification before
  // the Meta ad is created. The Promise resolver chain re-fires the
  // original add-to-campaign request with `confirmed_smartlink_url`.
  const [confirmSmartlink, setConfirmSmartlink] = useState<{
    suggestedUrl: string;
    artistName: string | null;
    releaseName: string | null;
    message: string | null;
    resolve: (url: string | null) => void;
  } | null>(null);
  const [bulkDeleteConfirmation, setBulkDeleteConfirmation] = useState<{
    isOpen: boolean;
    count: number;
  }>({
    isOpen: false,
    count: 0,
  });
  const [bulkDeleteInput, setBulkDeleteInput] = useState('');
  const [isScanningAudio, setIsScanningAudio] = useState(false);
  const [showUnreleased, setShowUnreleased] = useState(false);
  const [, setIsHoveringTable] = useState(false);
  const [lastSelectedIndex, setLastSelectedIndex] = useState<number | null>(null);
  const [showBulkReleaseModal, setShowBulkReleaseModal] = useState(false);
  const [autoLinkRunning, setAutoLinkRunning] = useState(false);
  
  const isProcessingThumbnails = useRef(false);
  const isProcessingAudio = useRef(false);
  const processedCreatives = useRef<Set<string>>(new Set());
  const [showBulkStatusMenu, setShowBulkStatusMenu] = useState(false);
  const [isBulkUpdatingStatus, setIsBulkUpdatingStatus] = useState(false);
  // Tracks which release-divider checkbox is currently fetching the full
  // server-side creative list so the divider can show a spinner while the
  // request resolves. Server-side pagination means the rows visible on the
  // page are only a slice of the release \u2014 we have to read the rest from
  // Postgres before we can add them all to `selectedCreatives`.
  const [loadingReleaseSelect, setLoadingReleaseSelect] = useState<string | null>(null);
  // Cache of release_id \u2192 full id list so toggling the checkbox a second
  // time (uncheck) doesn't have to round-trip to the DB again.
  const releaseIdsCache = useRef<Map<string, string[]>>(new Map());
  // Per-row Download spinner + bulk-zip progress for the new Download buttons.
  // `downloadingId` tracks a single row currently resolving/downloading.
  // `bulkDownloadProgress` shows "x/y" while building the TikTok zip.
  const [downloadingId, setDownloadingId] = useState<string | null>(null);
  const [bulkDownloadProgress, setBulkDownloadProgress] = useState<{
    done: number;
    total: number;
  } | null>(null);

  // Resolve a permanently-stored video URL for a TikTok Spark Ad creative.
  // If `tiktok_post_video_stored_url` is missing on the row, call the
  // download-tiktok-spark-ad-media edge function on the reporting project to
  // mirror the file into our videos bucket, then read the row back from
  // Postgres to get the freshly-written stored URL.
  const resolveTikTokVideoUrl = async (creative: any): Promise<string | null> => {
    if (creative.tiktok_post_video_stored_url) {
      return creative.tiktok_post_video_stored_url as string;
    }
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const token = session?.access_token || REPORTING_SUPABASE_ANON_KEY;
      const res = await fetch(
        `${REPORTING_SUPABASE_URL}/functions/v1/download-tiktok-spark-ad-media`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ creative_id: creative.id }),
        },
      );
      const body = await res.json().catch(() => ({}));
      const stored =
        body?.results?.[0]?.video_stored_url ||
        body?.video_stored_url ||
        null;
      if (stored) {
        useStore.setState((state: any) => ({
          adCreatives: state.adCreatives.map((x: any) =>
            String(x.id) === String(creative.id)
              ? { ...x, tiktok_post_video_stored_url: stored }
              : x,
          ),
        }));
        return stored;
      }
      // Fall back to a fresh DB read in case the edge function wrote the
      // column but didn't echo it back in the response shape we expect.
      const { data } = await supabase
        .from('ad_creatives')
        .select('tiktok_post_video_stored_url')
        .eq('id', creative.id)
        .single();
      return (data as any)?.tiktok_post_video_stored_url || null;
    } catch (err) {
      console.error('resolveTikTokVideoUrl failed for', creative.id, err);
      return null;
    }
  };

  // Pick the best stored-video URL for a creative, preferring direct
  // Supabase uploads (content) and falling back to the TikTok mirror.
  const getStoredVideoUrl = (creative: any): string | null => {
    if (isSupabaseStorageUrl(creative.content || '')) return creative.content;
    if (creative.tiktok_post_video_stored_url) {
      return creative.tiktok_post_video_stored_url;
    }
    return null;
  };

  // Strip every character that isn't alphanumeric, dash or underscore so
  // file system / download dialogs accept the resulting name (TikTok auth
  // codes contain `#`, `+`, `/`, `=` which are all illegal on macOS/Windows
  // and many cloud sync clients).
  const sanitizeFilenamePart = (s: string): string =>
    s.replace(/[^a-z0-9_-]+/gi, '').slice(0, 80);

  // Extract the Instagram short code from a reel/post URL. Returns null if
  // the URL doesn't match the expected `/reel(s)?/CODE` or `/p/CODE` shape.
  const extractInstagramShortcode = (url?: string | null): string | null => {
    if (!url) return null;
    const m = url.match(/instagram\.com\/(?:[^\/]+\/)?(?:reels?|p)\/([^\/?#]+)/i);
    return m?.[1] || null;
  };

  // Build a deterministic, special-char-free filename for one creative
  // following the format `<platform>_<code>.<ext>` so files in the bulk
  // zip are easy to scan.
  //   • TikTok      → tiktok_<authCodeWithoutSymbols>
  //   • Instagram   → instagram_<shortcode>
  //   • Direct file → direct_<videoNameOrId>
  const buildCreativeFilename = (creative: any, url: string): string => {
    const ext =
      (url.split('?')[0].split('.').pop() || 'mp4').toLowerCase().slice(0, 5);
    let platform: string;
    let code: string;
    const isTikTok =
      creative.platform === 'tiktok' || !!creative.mergedTiktokAuthCode;
    const isInstagram =
      creative.platform === 'instagram' ||
      !!creative.mergedInstagramReelUrl ||
      (creative.content || '').includes('instagram.com');
    if (isTikTok) {
      platform = 'tiktok';
      const raw =
        creative.mergedTiktokAuthCode ||
        creative.content ||
        String(creative.id);
      code = sanitizeFilenamePart(raw);
    } else if (isInstagram) {
      platform = 'instagram';
      const shortcode =
        extractInstagramShortcode(creative.mergedInstagramReelUrl) ||
        extractInstagramShortcode(creative.content) ||
        creative.videoName ||
        String(creative.id);
      code = sanitizeFilenamePart(shortcode);
    } else {
      platform = 'direct';
      code = sanitizeFilenamePart(
        creative.videoName || String(creative.id),
      );
    }
    if (!code) code = String(creative.id);
    return `${platform}_${code}.${ext}`;
  };

  // Trigger a browser download for a single creative. Resolves the storage
  // URL on demand for TikTok rows (calls download-tiktok-spark-ad-media),
  // then streams the file as a blob so the browser writes it to disk with
  // a sensible filename instead of opening it in a new tab.
  const handleDownloadCreative = async (creative: any) => {
    setDownloadingId(String(creative.id));
    try {
      let url: string | null = getStoredVideoUrl(creative);
      if (!url && creative.platform === 'tiktok') {
        url = await resolveTikTokVideoUrl(creative);
      }
      if (!url) {
        toast.error('No stored video file available for this creative.');
        return;
      }
      const filename = buildCreativeFilename(creative, url);
      // Supabase Storage/Dropbox URLs can force a native download via
      // ?download= / ?dl=1 — no fetch()+blob needed, which avoids the
      // network-level failures (blocked fetches, quota limits) entirely.
      const downloadUrl = buildForcedDownloadUrl(url, filename);
      if (downloadUrl !== url) {
        const a = document.createElement('a');
        a.href = downloadUrl;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        a.remove();
        return;
      }
      const res = await fetch(url);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      const objectUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = objectUrl;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(objectUrl), 5000);
    } catch (err: any) {
      console.error('Download failed for', creative.id, err);
      toast.error(`Download failed: ${err?.message || 'unknown error'}`);
    } finally {
      setDownloadingId(null);
    }
  };

  // Bulk-download every selected creative that has (or can have) a stored
  // video file as a single zip. Covers direct Supabase uploads, TikTok
  // Spark Ads (resolved on demand via download-tiktok-spark-ad-media) and
  // any other row whose `content` is already a Supabase storage URL. Rows
  // without a stored file (Instagram-only, etc.) are skipped.
  const handleBulkDownloadVideos = async () => {
    // Selected IDs may include creatives that aren't on the current page
    // (release-divider checkbox now selects every row of the release across
    // all pages). Fetch the missing rows from Postgres so the zip really
    // contains every selected file, not just what's currently rendered.
    const selectedIds = Array.from(selectedCreatives);
    const knownById = new Map<string, any>();
    for (const c of filteredCreatives as any[]) {
      knownById.set(String(c.id), c);
    }
    const missingIds = selectedIds.filter((id) => !knownById.has(String(id)));
    let extraRows: any[] = [];
    if (missingIds.length > 0) {
      try {
        const { data, error } = await supabase
          .from('ad_creatives')
          .select(
            '*, releases(id, name, release_date)',
          )
          .in('id' as any, missingIds as any);
        if (error) throw error;
        extraRows = (data as any[]) || [];
      } catch (err) {
        console.error('Failed to fetch off-page creatives for bulk download', err);
        toast.error('Failed to load full selection');
        return;
      }
    }
    const allSelected = [...(filteredCreatives as any[]), ...extraRows].filter(
      (c) => selectedCreatives.has(String(c.id)),
    );
    const candidates = allSelected.filter((c: any) => {
      if (isSupabaseStorageUrl(c.content || '')) return true;
      if (c.tiktok_post_video_stored_url) return true;
      if (c.platform === 'tiktok' || !!c.mergedTiktokAuthCode) return true;
      return false;
    });
    if (candidates.length === 0) {
      toast.error('No downloadable video files in selection');
      return;
    }
    setBulkDownloadProgress({ done: 0, total: candidates.length });
    const toastId = toast.loading(
      `Preparing 0/${candidates.length} videos…`,
    );
    try {
      const zip = new JSZip();
      let done = 0;
      const failed: string[] = [];
      for (const c of candidates) {
        try {
          let url = getStoredVideoUrl(c);
          if (!url && (c.platform === 'tiktok' || !!c.mergedTiktokAuthCode)) {
            url = await resolveTikTokVideoUrl(c);
          }
          if (!url) {
            failed.push(c.videoName || String(c.id));
            continue;
          }
          const res = await fetch(url);
          if (!res.ok) throw new Error(`HTTP ${res.status}`);
          const blob = await res.blob();
          let filename = buildCreativeFilename(c, url);
          // Guard against the (rare) case of two creatives producing the
          // same code-based name — append the row id so the zip never
          // silently overwrites entries.
          if (zip.file(filename)) {
            const dot = filename.lastIndexOf('.');
            filename =
              filename.slice(0, dot) +
              `_${c.id}` +
              filename.slice(dot);
          }
          zip.file(filename, blob);
        } catch (err) {
          console.error('zip entry failed for', c.id, err);
          failed.push(c.videoName || String(c.id));
        }
        done += 1;
        setBulkDownloadProgress({ done, total: candidates.length });
        toast.loading(
          `Preparing ${done}/${candidates.length} videos…`,
          { id: toastId },
        );
      }
      const blob = await zip.generateAsync({ type: 'blob' });
      // If every selected creative belongs to the same release, name the
      // zip after that release. Mixed selections (or "No Release") fall
      // back to a timestamped generic name.
      const uniqueReleaseIds = new Set(
        candidates.map((c: any) => c.release_id || null),
      );
      let zipName: string;
      if (uniqueReleaseIds.size === 1) {
        const onlyId = [...uniqueReleaseIds][0];
        const releaseName = onlyId
          ? (candidates.find((c: any) => c.release_id === onlyId) as any)?.releases?.name
          : null;
        zipName = releaseName
          ? `${sanitizeFilenamePart(releaseName)}.zip`
          : `ad-creatives_${format(new Date(), 'yyyy-MM-dd_HHmm')}.zip`;
      } else {
        zipName = `ad-creatives_${format(new Date(), 'yyyy-MM-dd_HHmm')}.zip`;
      }
      const objectUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = objectUrl;
      a.download = zipName;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(objectUrl), 5000);
      if (failed.length > 0) {
        toast.error(
          `Downloaded ${candidates.length - failed.length}/${candidates.length} — ${failed.length} failed`,
          { id: toastId },
        );
      } else {
        toast.success(
          `Downloaded ${candidates.length} video${candidates.length === 1 ? '' : 's'}`,
          { id: toastId },
        );
      }
    } catch (err: any) {
      console.error('Bulk download failed', err);
      toast.error(`Download failed: ${err?.message || 'unknown error'}`, {
        id: toastId,
      });
    } finally {
      setBulkDownloadProgress(null);
    }
  };

  // Update selectedArtistId when artistId prop changes
  useEffect(() => {
    if (artistId) {
      setSelectedArtistId(artistId);
    }
  }, [artistId, setSelectedArtistId]);

  // Reset to page 1 when filters change
  useEffect(() => {
    if (isInitialized) {
      setAdCreativesPage(1);
    }
  }, [selectedArtistId, selectedPlatform, selectedStatus, isInitialized, setAdCreativesPage]);

  // Fetch data when page or filters change
  useEffect(() => {
    console.log('🎨 AdCreatives page - loading data');
    console.log('🎨 artistId prop:', artistId);
    console.log('🎨 selectedArtistId state:', selectedArtistId);
    console.log('🎨 URL artist param (searchParams):', searchParams.get('artist'));
    console.log('🎨 URL artist param (urlParams):', urlParams.artist);
    console.log('🎨 location.search:', location.search);
    console.log('🎨 All URL params (searchParams):', Object.fromEntries(searchParams.entries()));
    
    // Platform/status come straight from state — it is seeded from the URL
    // on first render and mirrored back to it, so it is always current.
    // Artist still reads the URL first because the store value only arrives
    // via an effect and would be empty on the initial render.
    const filters = {
      artistId: urlParams.artist || artistId || selectedArtistId || undefined,
      platform: selectedPlatform || undefined,
      status: selectedStatus || undefined
    };
    
    console.log('🎨 Final filters object:', JSON.stringify(filters));
    console.log('📊 Fetching ad creatives and artists...');
    // Fetch ad creatives with pagination and filters
    fetchAdCreatives(adCreativesPagination.currentPage, adCreativesPagination.pageSize, filters);
    fetchArtists();
    console.log('✅ AdCreatives data fetch initiated');
    
    // Only run initialization once on mount
    if (!isInitialized) {
      setIsInitialized(true);
      setModalInitialized(true);
    }
    // urlParams.platform/status are deliberately not dependencies: they no
    // longer feed `filters`, and including them would fire a second,
    // identical fetch as soon as the mirror effect rewrites the URL.
  }, [adCreativesPagination.currentPage, adCreativesPagination.pageSize, urlParams.artist, selectedArtistId, selectedPlatform, selectedStatus]);

  // Fetch TikTok campaign operation_status (ENABLE/DISABLE) for every
  // release visible on the current page that has a linked TikTok campaign.
  // The edge function writes the status back to the releases table, so the
  // next render picks it up via the nested releases object and
  // filteredCreatives hides pending creatives for disabled campaigns.
  const tiktokCampaignStatusFetchedRef = useRef<Set<string>>(new Set());
  useEffect(() => {
    const campaignIds = new Set<string>();
    for (const c of adCreatives) {
      const cid = (c as any).releases?.tiktok_campaign_id as string | undefined;
      const status = (c as any).releases?.tiktok_campaign_status as string | undefined;
      if (cid && !status && !tiktokCampaignStatusFetchedRef.current.has(cid)) {
        campaignIds.add(cid);
        tiktokCampaignStatusFetchedRef.current.add(cid);
      }
    }
    if (campaignIds.size === 0) return;

    const ids = Array.from(campaignIds);
    fetch(`${REPORTING_SUPABASE_URL}/functions/v1/get-tiktok-campaign-status`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${REPORTING_SUPABASE_ANON_KEY}`,
      },
      body: JSON.stringify({ campaign_ids: ids }),
    }).then(async (res) => {
      const data = await res.json().catch(() => ({}));
      if (data?.success && data.statuses) {
        // Patch local state so the UI updates without a full refetch.
        useStore.setState((state: any) => ({
          adCreatives: state.adCreatives.map((c: any) => {
            const cid = c.releases?.tiktok_campaign_id;
            if (cid && data.statuses[cid]) {
              return {
                ...c,
                releases: { ...c.releases, tiktok_campaign_status: data.statuses[cid] },
              };
            }
            return c;
          }),
        }));
      }
    }).catch((err) => console.error('Failed to fetch TikTok campaign status:', err));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [adCreatives]);

  // ── Auto-link releases on page load ──────────────────────────────
  // Calls the auto-link-releases edge function once per artist to
  // automatically link song.so smartlinks, reports, Meta campaigns, and
  // TikTok campaigns for releases that are missing them. Releases that
  // can't be auto-linked are shown in a modal with quick-link buttons.
  const autoLinkRanRef = useRef<string | null>(null);
  useEffect(() => {
    const currentArtistId = urlParams.artist || artistId || selectedArtistId || 'all';
    if (autoLinkRanRef.current === currentArtistId) return;
    autoLinkRanRef.current = currentArtistId;

    setAutoLinkRunning(true);
    const runAutoLink = async () => {
      try {
        const res = await fetch(
          `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/auto-link-releases`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
            },
            body: JSON.stringify(currentArtistId === 'all' ? {} : { artist_id: currentArtistId }),
          },
        );
        const data = await res.json().catch(() => ({}));
        if (data?.success) {
          const linked = data.linked || {};
          const totalLinked =
            (linked.smartlink || 0) + (linked.report || 0) +
            (linked.meta || 0) + (linked.tiktok || 0);
          if (totalLinked > 0) {
            toast.success(
              `Auto-linked: ${linked.smartlink || 0} smartlinks, ${linked.report || 0} reports, ${linked.meta || 0} Meta, ${linked.tiktok || 0} TikTok campaigns`,
            );
            // Refetch creatives to pick up the newly linked data
            const filters = {
              artistId: currentArtistId === 'all' ? undefined : currentArtistId,
              platform: selectedPlatform || undefined,
              status: selectedStatus || undefined,
            };
            fetchAdCreatives(adCreativesPagination.currentPage, adCreativesPagination.pageSize, filters);
          }
        }
      } catch (err) {
        console.error('Auto-link failed:', err);
      } finally {
        setAutoLinkRunning(false);
      }
    };
    runAutoLink();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [urlParams.artist, artistId, selectedArtistId]);

  // NOTE: Auto-add-creatives is NO LONGER triggered on page load.
  // It caused issues with creatives being auto-added when the admin
  // was just browsing. Auto-add now runs exclusively via pg_cron.
  // Auto-link-releases still runs on page load (it only links
  // campaigns/smartlinks, doesn't add creatives to platforms).

  // Auto-generate thumbnails for videos without them on current page
  useEffect(() => {
    const generateMissingThumbnails = async () => {
      if (isProcessingThumbnails.current) return;
      isProcessingThumbnails.current = true;
      
      try {
        // Only check creatives currently loaded on this page
        const currentPageCreatives = adCreatives.filter(creative => 
          creative.content && 
          isSupabaseStorageUrl(creative.content) && 
          (!creative.thumbnail_url || creative.thumbnail_url.startsWith('data:image/')) &&
          !processedCreatives.current.has(creative.id.toString())
        );

        if (currentPageCreatives.length === 0) return;

        console.log(`Found ${currentPageCreatives.length} creatives on page ${adCreativesPagination.currentPage} without thumbnails`);

        // Generate thumbnails for Supabase storage videos (client-side)
        for (const creative of currentPageCreatives.slice(0, 3)) { // Limit to 3 at a time
          try {
            console.log(`Generating thumbnail for Supabase video ${creative.id}`);
            const thumbnailUrl = await generateThumbnailFromUrl(creative.content);
            
            if (thumbnailUrl) {
              await supabase
                .from('ad_creatives')
                .update({
                  thumbnail_url: thumbnailUrl,
                  updated_at: new Date().toISOString()
                } as any)
                .eq('id' as any, creative.id as any);

              processedCreatives.current.add(creative.id.toString());
              console.log(`Thumbnail generated and saved for creative ${creative.id}`);

              // Patch the single creative so the row's thumbnail updates
              // live without a full table refetch (which flickers).
              useStore.setState((state: any) => ({
                adCreatives: state.adCreatives.map((x: any) =>
                  String(x.id) === String(creative.id)
                    ? { ...x, thumbnail_url: thumbnailUrl }
                    : x,
                ),
              }));

              // Small delay before next one
              await new Promise(resolve => setTimeout(resolve, 500));
            }
          } catch (error) {
            console.error(`Error generating thumbnail for creative ${creative.id}:`, error);
          }
        }

        // No full fetchAdCreatives refresh — the per-creative
        // useStore.setState patch above already updated the visible
        // rows. A full refetch was causing table flickering because it
        // replaces the entire adCreatives array and re-sorts
        // client-side on every batch.
      } catch (error) {
        console.error('Error auto-generating thumbnails:', error);
      } finally {
        isProcessingThumbnails.current = false;
      }
    };

    generateMissingThumbnails();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [adCreativesPagination.currentPage, adCreatives]); // Run when creatives are loaded

  // Auto-scan Instagram audio type for unscanned IG creatives on current page
  useEffect(() => {
    const scanMissingAudio = async () => {
      if (isProcessingAudio.current) return;
      isProcessingAudio.current = true;
      
      try {
        const targets = adCreatives.filter((c: any) =>
          c.platform === 'instagram' &&
          !c.audio_type &&
          !processedCreatives.current.has(c.id.toString()) &&
          (c.mergedInstagramReelUrl?.includes('instagram.com') || c.content?.includes('instagram.com'))
        );
        if (targets.length === 0) return;

        const batch = targets.slice(0, 5); // 5 per page-load burst to be kind to RapidAPI
        console.log(`Auto-scanning audio for ${batch.length} IG creatives on page ${adCreativesPagination.currentPage}`);

        let licensedDownloads = 0;
        for (const c of batch) {
          try {
            const url = (c as any).mergedInstagramReelUrl || c.content;
            const result = await scanInstagramAudio(c.id, url);
            if (result?.licensed && result?.downloaded) licensedDownloads++;
            processedCreatives.current.add(c.id.toString());
          } catch (err) {
            console.error('Auto-scan error for', c.id, err);
          }
          // Light throttle between calls
          await new Promise((r) => setTimeout(r, 300));
        }

        // Refresh list so new audio_type / replaced URLs show up with current filters from URL
        const currentArtistId = urlParams.artist || artistId || selectedArtistId || undefined;
        const currentPlatform = selectedPlatform || undefined;
        const currentStatus = selectedStatus || undefined;
        const filters = {
          artistId: currentArtistId,
          platform: currentPlatform,
          status: currentStatus,
        };
        fetchAdCreatives(adCreativesPagination.currentPage, adCreativesPagination.pageSize, filters);

        if (licensedDownloads > 0) {
          toast.success(
            `${licensedDownloads} licensed reel${licensedDownloads === 1 ? '' : 's'} downloaded to storage`,
            { duration: 5000 }
          );
        }
      } catch (error) {
        console.error('Error auto-scanning audio:', error);
      } finally {
        isProcessingAudio.current = false;
      }
    };

    if (adCreatives.length > 0 && !loading) {
      scanMissingAudio();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [adCreativesPagination.currentPage, adCreatives, loading]);

  // Auto-fetch Instagram thumbnails for creatives that have audio_type but no thumbnail_url
  useEffect(() => {
    const fetchMissingThumbnails = async () => {
      if (isProcessingAudio.current) return; // Reuse the same flag to avoid conflicts
      
      try {
        console.log('🖼️ Auto-fetch thumbnails - checking for IG creatives with audio_type but no thumbnail_url');
        console.log('🖼️ Total creatives on page:', adCreatives.length);
        console.log('🖼️ Loading state:', loading);
        
        // A thumbnail counts as "missing" if it's null/undefined OR still
        // points at the expiring Instagram CDN (fbcdn.net / cdninstagram.com).
        // We always want to mirror those into our `thumbnails` bucket.
        const isExpiringIgCdnUrl = (url: string | null | undefined) =>
          !!url && (url.includes('fbcdn.net') || url.includes('cdninstagram.com'));

        const targets = adCreatives.filter((c: any) => {
          // Include direct_upload rows whose video was downloaded from IG
          // — they still have an `original_instagram_url` we can use to
          // re-fetch the poster.
          const isInstagram =
            c.platform === 'instagram' ||
            (c.platform === 'direct_upload' && c.original_instagram_url?.includes('instagram.com'));
          const hasAudioType = c.audio_type;
          const thumb = c.instagram_thumbnail_url;
          const needsThumbnail = !thumb || isExpiringIgCdnUrl(thumb);
          const notProcessed = !processedCreatives.current.has(c.id.toString());
          const hasInstagramUrl =
            c.mergedInstagramReelUrl?.includes('instagram.com') ||
            c.content?.includes('instagram.com') ||
            c.original_instagram_url?.includes('instagram.com');
          
          console.log(`🖼️ Creative ${c.id}: platform=${c.platform}, audio_type=${c.audio_type}, instagram_thumbnail_url=${thumb}, needsThumbnail=${needsThumbnail}, hasInstagramUrl=${hasInstagramUrl}`);
          
          return isInstagram && hasAudioType && needsThumbnail && notProcessed && hasInstagramUrl;
        });
        
        console.log(`🖼️ Found ${targets.length} IG creatives needing thumbnails on page ${adCreativesPagination.currentPage}`);
        
        if (targets.length === 0) return;

        const batch = targets.slice(0, 5); // 5 per page-load burst
        console.log(`Auto-fetching thumbnails for ${batch.length} IG creatives on page ${adCreativesPagination.currentPage}`);

        // Mark every creative as processed BEFORE any awaits so concurrent
        // re-renders (caused by the refresh fetchAdCreatives call at the
        // bottom of this effect) don't re-enqueue the same IDs in parallel.
        for (const c of batch) processedCreatives.current.add(c.id.toString());
        for (const c of batch) {
          try {
            const url =
              ((c as any).mergedInstagramReelUrl?.includes('instagram.com') && (c as any).mergedInstagramReelUrl) ||
              (c.content?.includes('instagram.com') && c.content) ||
              (c as any).original_instagram_url;
            console.log(`🖼️ Fetching thumbnail for creative ${c.id} from URL: ${url}`);
            const thumbnailUrl = await fetchInstagramThumbnail(url, c.id);
            if (thumbnailUrl) {
              console.log(`✅ Auto-fetched thumbnail for creative ${c.id}: ${thumbnailUrl}`);
              // Optimistically patch the single creative in the store so
              // the row's thumbnail updates instantly without waiting for
              // the full refresh at the end of the batch.
              useStore.setState((state: any) => ({
                adCreatives: state.adCreatives.map((x: any) =>
                  String(x.id) === String(c.id)
                    ? { ...x, instagram_thumbnail_url: thumbnailUrl }
                    : x,
                ),
              }));
            } else {
              console.log(`⚠️ No thumbnail returned for creative ${c.id}`);
            }
          } catch (err) {
            console.error('❌ Auto-fetch thumbnail error for', c.id, err);
          }
          // Light throttle between calls
          await new Promise((r) => setTimeout(r, 300));
        }

        // No full fetchAdCreatives refresh — the per-creative
        // useStore.setState patch above already updated the visible
        // rows. A full skipCache refetch was causing table flickering
        // because it replaces the entire adCreatives array and
        // re-sorts client-side on every batch.
      } catch (error) {
        console.error('Error auto-fetching thumbnails:', error);
      }
    };

    if (adCreatives.length > 0 && !loading) {
      fetchMissingThumbnails();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [adCreativesPagination.currentPage, adCreatives, loading]);

  // Auto-fetch TikTok Spark Ad thumbnails for creatives with an auth code
  // but no tiktok_thumbnail_url yet. Calls download-tiktok-spark-ad-media
  // on the reporting project, which authorizes the auth code, fetches post
  // info (poster_url via tt_video/info) and permanently mirrors the poster
  // into our thumbnails bucket + writes tiktok_thumbnail_url.
  useEffect(() => {
    const fetchMissingTikTokThumbnails = async () => {
      try {
        const isExpiringTiktokCdnUrl = (url: string | null | undefined) =>
          !!url && (url.includes('tiktokcdn.com') || url.includes('tiktokcdn-us.com') || url.includes('.tiktokv.com'));

        const targets = adCreatives.filter((c: any) => {
          const isTiktok = c.platform === 'tiktok';
          const hasAuthCode =
            !!c.mergedTiktokAuthCode ||
            (typeof c.content === 'string' && c.content.startsWith('#'));
          const thumb = c.tiktok_thumbnail_url;
          const needsThumbnail = !thumb || isExpiringTiktokCdnUrl(thumb);
          const notProcessed = !processedCreatives.current.has(`tt_${c.id}`);
          return isTiktok && hasAuthCode && needsThumbnail && notProcessed;
        });

        console.log(`🎵 Found ${targets.length} TikTok Spark Ad creatives needing thumbnails`);
        if (targets.length === 0) return;

        const batch = targets.slice(0, 5); // 5 per page-load burst
        // Mark every creative in the batch as processed BEFORE any awaits,
        // so concurrent re-renders of this effect (triggered by the
        // fetchAdCreatives refresh below) don't re-enqueue the same IDs.
        for (const c of batch) processedCreatives.current.add(`tt_${c.id}`);
        for (const c of batch) {
          try {
            console.log(`🎵 Fetching TikTok poster for creative ${c.id}`);
            const res = await fetch(
              `${REPORTING_SUPABASE_URL}/functions/v1/download-tiktok-spark-ad-media`,
              {
                method: 'POST',
                headers: {
                  'Content-Type': 'application/json',
                  Authorization: `Bearer ${REPORTING_SUPABASE_ANON_KEY}`,
                },
                body: JSON.stringify({ creative_id: c.id, poster_only: true }),
              },
            );
            const body = await res.json().catch(() => ({}));
            const r = body?.results?.[0];
            if (r?.poster_stored_url) {
              console.log(`✅ TikTok poster stored for creative ${c.id}: ${r.poster_stored_url}`);
              // Patch the single creative so the row's thumbnail updates
              // live instead of waiting for the full refresh below.
              useStore.setState((state: any) => ({
                adCreatives: state.adCreatives.map((x: any) =>
                  String(x.id) === String(c.id)
                    ? { ...x, tiktok_thumbnail_url: r.poster_stored_url }
                    : x,
                ),
              }));
            } else {
              console.log(`⚠️ No TikTok poster for creative ${c.id}: ${r?.status || body?.error}`);
            }
          } catch (err) {
            console.error('❌ TikTok poster fetch error for', c.id, err);
          }
          await new Promise((r) => setTimeout(r, 300));
        }

        // No full fetchAdCreatives refresh here — the per-creative
        // useStore.setState patch above already updated the visible rows
        // with their new tiktok_thumbnail_url. A full skipCache refetch
        // (which fetches ALL 1800+ TikTok creatives in admin view) was
        // causing the table to flicker on every 5-creative batch because
        // it replaced the entire adCreatives array and re-sorted
        // client-side. The thumbnails are persisted to the DB by the edge
        // function, so the next page load or manual refresh picks them up.
      } catch (error) {
        console.error('Error auto-fetching TikTok thumbnails:', error);
      }
    };

    if (adCreatives.length > 0 && !loading) {
      fetchMissingTikTokThumbnails();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [adCreativesPagination.currentPage, adCreatives, loading]);

  // Auto-scan Instagram creatives on the current page for missing audio_type and thumbnails
  useEffect(() => {
    const scanPageCreatives = async () => {
      if (!selectedArtistId || isProcessingAudio.current) return;
      isProcessingAudio.current = true;

      try {
        console.log('🔄 Auto-scanning Instagram creatives on current page for missing audio_type and thumbnails');
        
        // Only scan creatives currently visible on the page. Treat
        // expired Instagram CDN URLs (fbcdn.net / cdninstagram.com) the
        // same as missing so we re-mirror them into our `thumbnails`
        // bucket on page load.
        const isExpiringIgCdnUrl = (url: string | null | undefined) =>
          !!url && (url.includes('fbcdn.net') || url.includes('cdninstagram.com'));
        const creativesToScan = adCreatives.filter(c => 
          c.platform === 'instagram' && 
          (!c.audio_type || !c.instagram_thumbnail_url || isExpiringIgCdnUrl(c.instagram_thumbnail_url)) &&
          c.mergedInstagramReelUrl
        );

        console.log(`Found ${creativesToScan.length} Instagram creatives on current page needing scan`);

        // Process in batches of 5 to avoid rate limiting
        const batchSize = 5;
        let licensedCount = 0;
        let originalCount = 0;
        let errorCount = 0;

        for (let i = 0; i < creativesToScan.length; i += batchSize) {
          const batch = creativesToScan.slice(i, i + batchSize);
          
          const results = await Promise.allSettled(
            batch.map(creative => 
              fetch('https://wrlgoxbzlngdtomjhvnz.supabase.co/functions/v1/scan-instagram-audio', {
                method: 'POST',
                headers: {
                  'Content-Type': 'application/json',
                  'Authorization': `Bearer ${(window as any).supabase?.auth?.session()?.access_token || ''}`
                },
                body: JSON.stringify({
                  creativeId: creative.id,
                  instagramUrl: creative.mergedInstagramReelUrl,
                  videoName: creative.videoName,
                  fetchThumbnailOnly: !creative.audio_type
                })
              }).then(res => res.json())
            )
          );

          results.forEach((result) => {
            if (result.status === 'fulfilled') {
              if (result.value.audio_type === 'licensed') licensedCount++;
              else if (result.value.audio_type === 'original') originalCount++;
            } else {
              errorCount++;
            }
          });

          // Add delay between batches
          if (i + batchSize < creativesToScan.length) {
            await new Promise(resolve => setTimeout(resolve, 1000));
          }
        }

        console.log(`✅ Scanned ${creativesToScan.length} creatives: ${licensedCount} licensed, ${originalCount} original, ${errorCount} errors`);
        
        // Refresh list to show updated audio_type and thumbnails
        const currentArtistId = urlParams.artist || artistId || selectedArtistId || undefined;
        const currentPlatform = selectedPlatform || undefined;
        const currentStatus = selectedStatus || undefined;
        const filters = {
          artistId: currentArtistId,
          platform: currentPlatform,
          status: currentStatus,
        };
        fetchAdCreatives(adCreativesPagination.currentPage, adCreativesPagination.pageSize, filters);

        if (licensedCount > 0) {
          toast.success(
            `${licensedCount} licensed reel${licensedCount === 1 ? '' : 's'} downloaded to storage`,
            { duration: 5000 }
          );
        }
      } catch (error) {
        console.error('Error auto-scanning Instagram creatives:', error);
      } finally {
        isProcessingAudio.current = false;
      }
    };

    // Trigger scan when artist is selected and data is loaded
    if (selectedArtistId && adCreatives.length > 0 && !processedCreatives.current.has('scanPage')) {
      processedCreatives.current.add('scanPage');
      scanPageCreatives();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedArtistId, adCreatives.length]);

  const getArtistName = (artistId: string) => {
    const artist = artists.find(a => a.id === artistId);
    return artist?.name || 'Unknown Artist';
  };
  
  const getArtistWhatsAppGroupId = (artistId: string) => {
    const artist = artists.find(a => a.id === artistId);
    return artist?.whatsappGroupId || null;
  };

  const getStatusBadgeClass = (status: string) => {
    switch (status) {
      case 'pending':
        return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-200';
      case 'active':
        return 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-200';
      case 'archived':
        return 'bg-gray-100 text-gray-800 dark:bg-gray-900/20 dark:text-gray-200';
      case 'rejected':
        return 'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-200';
      case 'error':
        return 'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-200';
      case 'expired':
        return 'bg-orange-100 text-orange-800 dark:bg-orange-900/20 dark:text-orange-200';
      default:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-900/20 dark:text-gray-200';
    }
  };

  // Creatives with expired TikTok auth codes have status="error" but
  // tiktok_ad_status="EXPIRED_AUTH_CODE". Show "Expired" instead of "Error"
  // so users know the creator needs to generate a new auth code.
  const getDisplayStatus = (creative: any): string => {
    if (creative.status === 'error' && creative.tiktok_ad_status === 'EXPIRED_AUTH_CODE') {
      return 'expired';
    }
    return creative.status;
  };

  // Direct add to campaign function (no modal)
  const handleAddToCampaign = async (creative: any, force: boolean = false) => {
    if (addingToCampaign) return; // Prevent double-click
    
    setAddingToCampaign(creative.id);
    
    try {
      // Get release info to find campaign by "Artist - Release" pattern
      if (!creative.release_id) {
        toast.error('Creative must be assigned to a release first');
        return;
      }

      // Fetch release with artist info and saved campaign
      const { data: release, error: releaseError } = await supabase
        .from('releases')
        .select('*, artists(id, name, facebook_page_id), release_campaign_links(smartlink_url)')
        .eq('id', creative.release_id)
        .single();

      if (releaseError || !release) {
        toast.error('Failed to fetch release info');
        return;
      }

      // A song.so smartlink linked to this release is authoritative — pass it
      // along so the edge function never falls through to the song.so lookup
      // and the confirmation dialog. PostgREST returns the embedded row as an
      // object (release_id is UNIQUE); handle both shapes defensively.
      const rcLinkRaw = (release as any).release_campaign_links;
      const rcLink = Array.isArray(rcLinkRaw) ? rcLinkRaw[0] : rcLinkRaw;
      const linkedSmartlinkUrl: string | null = rcLink?.smartlink_url || null;

      const artistName = (release as any).artists?.name || '';
      const releaseName = (release as any).name || '';
      const savedCampaignId = (release as any).meta_campaign_id;
      const savedCampaignName = (release as any).meta_campaign_name;
      const savedAccountId = (release as any).meta_account_id;

      let campaign: { campaign_id: string; campaign_name: string; account_id: string };

      // Verify saved campaign name matches the release before using it
      const savedCampaignMatchesRelease = savedCampaignName && 
        savedCampaignName.toLowerCase().includes(releaseName.toLowerCase());

      // Use saved campaign ID only if it matches the release name
      if (savedCampaignId && savedCampaignMatchesRelease) {
        campaign = {
          campaign_id: savedCampaignId,
          campaign_name: savedCampaignName || savedCampaignId,
          account_id: savedAccountId || '626419741407621' // Use saved account or default
        };
        console.log('Using saved campaign:', campaign.campaign_name, 'Account:', campaign.account_id);
      } else {
        if (savedCampaignId && !savedCampaignMatchesRelease) {
          console.log(`Saved campaign "${savedCampaignName}" doesn't match release "${releaseName}", searching fresh...`);
        }
        // Search for campaign by pattern "Artist - Release"
        const searchPattern = `${artistName} - ${releaseName}`;
        const { data: { session } } = await supabase.auth.getSession();
        const token = session?.access_token || REPORTING_SUPABASE_ANON_KEY;
        
        console.log('Using token type:', session ? 'user JWT' : 'anon key');
        console.log('Token preview:', token ? `${token.substring(0, 20)}...` : 'null');
        
        let searchResponse = await fetch(`${REPORTING_SUPABASE_URL}/functions/v1/search-meta-campaigns`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
          },
          body: JSON.stringify({
            search_pattern: searchPattern,
            account_id: '626419741407621'
          }),
        });

        if (!searchResponse.ok) {
          const errorText = await searchResponse.text();
          console.error('Failed to search for campaigns:', searchResponse.status, errorText);
          toast.error(`Failed to search for campaigns: ${searchResponse.status}`);
          return;
        }

        let searchResult = await searchResponse.json();
        let campaigns = searchResult.campaigns || [];

        // Fallback: If no campaign found, search by artist name only and check ad sets for release name
        if (campaigns.length === 0 && artistName) {
          console.log(`No campaign found for "${searchPattern}", trying fallback search by artist: ${artistName}`);
          
          searchResponse = await fetch(`${REPORTING_SUPABASE_URL}/functions/v1/search-meta-campaigns`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`,
            },
            body: JSON.stringify({
              search_pattern: artistName,
              account_id: '626419741407621',
              check_adsets_for_release: releaseName // Pass release name to check in ad sets
            }),
          });

          if (searchResponse.ok) {
            searchResult = await searchResponse.json();
            campaigns = searchResult.campaigns || [];
            if (campaigns.length > 0) {
              console.log(`Found campaign via artist fallback: ${campaigns[0].campaign_name}`);
            }
          }
        }

        if (campaigns.length === 0) {
          toast.error(`No campaign found matching "${searchPattern}" or artist "${artistName}"`);
          return;
        }

        campaign = campaigns[0];
        
        // Save the campaign and account to the release for future use
        await supabase
          .from('releases')
          .update({ 
            meta_campaign_id: campaign.campaign_id,
            meta_campaign_name: campaign.campaign_name,
            meta_account_id: campaign.account_id
          } as any)
          .eq('id' as any, creative.release_id as any);
      }
      
      // Skip audio validation: always prefer the REAL Instagram post so the
      // creative is promoted as a regular ad via source_instagram_media_id
      // (the same flow used for organic reels), never as a file/dark ad.
      // The audio scan used to overwrite content/merged_instagram_reel_url
      // with a Supabase storage URL when it detected licensed music; we now
      // preserve original_instagram_url and recover it here. For rows that
      // were already overwritten, original_instagram_url still holds the
      // real IG post URL.
      const originalIgUrl = creative.original_instagram_url?.includes('instagram.com/')
        ? creative.original_instagram_url
        : null;
      const mergedIgUrl = creative.mergedInstagramReelUrl?.includes('instagram.com/')
        ? creative.mergedInstagramReelUrl
        : null;
      const contentIgUrl = creative.content?.includes('instagram.com/')
        ? creative.content
        : null;

      // Prefer the original IG post URL, then merged, then content.
      const instagramUrlToUse = originalIgUrl || mergedIgUrl || contentIgUrl || null;

      // If there's an Instagram Reel URL (even with a file), use the Instagram post flow
      const hasInstagramReelUrl = !!instagramUrlToUse;

      // Determine creative type. Audio validation is skipped, so a Supabase
      // storage URL in `content` no longer forces the file path — when we
      // have an IG post URL we always promote the real IG post. Only treat
      // as a file creative when we genuinely have no IG post URL to promote.
      const isFileCreative =
        !hasInstagramReelUrl ||
        creative.platform === 'dropbox' ||
        creative.platform === 'tiktok';

      // Instagram posts always use the IG post flow — even when content is a
      // Supabase storage URL (licensed-replaced), we recover the original post.
      const isInstagramPost = hasInstagramReelUrl;

      // Determine the creative URL/ID to use
      let creativeUrl = creative.content;
      let instagramPostId: string | null = null;

      if (isInstagramPost && instagramUrlToUse) {
        // Match /reel/, /reels/, /p/, /tv/ patterns
        const match = instagramUrlToUse.match(/\/(?:reels?|p|tv)\/([A-Za-z0-9_-]+)/);
        if (match) {
          instagramPostId = match[1];
        }
        creativeUrl = instagramUrlToUse;
        console.log('Instagram URL:', instagramUrlToUse, 'Extracted shortcode:', instagramPostId);
      }

      // This creative's own thumbnail. Without it the edge function has to
      // fall back to a thumbnail scraped off an existing ad in the campaign,
      // which shows a different video's frame.
      const creativeThumbnailUrl =
        creative.thumbnail_url ||
        creative.instagram_thumbnail_url ||
        creative.tiktok_thumbnail_url ||
        null;

      // Call the edge function to add creative to campaign
      toast.loading('Adding to campaign...', { id: 'add-campaign' });
      
      const { data: { session } } = await supabase.auth.getSession();
      const token = session?.access_token || import.meta.env.VITE_SUPABASE_ANON_KEY;

      // The edge function may return 409 with `requires_smartlink_confirmation`
      // when no landing URL could be auto-resolved and song.so suggested
      // one. In that case we prompt the user and re-submit with
      // `confirmed_smartlink_url` set to whatever they accept.
      const callAddToCampaign = (extraBody: Record<string, unknown> = {}) =>
        fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/add-creative-to-meta-campaign`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
          },
          body: JSON.stringify({
            campaign_id: campaign.campaign_id,
            account_id: campaign.account_id,
            creative_type: isFileCreative ? 'file' : 'instagram_post',
            creative_url: creativeUrl,
            instagram_post_id: instagramPostId,
            creative_name: creative.videoName || `Creative ${creative.id}`,
            creative_id: creative.id,
            thumbnail_url: creativeThumbnailUrl,
            release_id: creative.release_id || null,
            release_name: (release as any)?.name || null,
            artist_name: (release as any)?.artists?.name || null,
            campaign_name: campaign.campaign_name || null,
            smartlink_url: linkedSmartlinkUrl,
            facebook_page_id: (release as any)?.artists?.facebook_page_id || null,
            force,
            ...extraBody,
          }),
        });

      let response = await callAddToCampaign();
      let result = await response.json();

      if (response.status === 409 && result?.requires_smartlink_confirmation) {
        toast.dismiss('add-campaign');
        // Open the React modal and wait for the user to confirm or cancel.
        const confirmed = await new Promise<string | null>((resolve) => {
          setConfirmSmartlink({
            suggestedUrl: result.suggested_smartlink_url,
            artistName: result.artist_name || null,
            releaseName: result.release_name || null,
            message: result.message || null,
            resolve,
          });
        });
        setConfirmSmartlink(null);
        if (!confirmed) {
          throw new Error('Aborted: smartlink not confirmed');
        }
        toast.loading('Adding to campaign...', { id: 'add-campaign' });
        response = await callAddToCampaign({ confirmed_smartlink_url: confirmed });
        result = await response.json();
      }

      if (!response.ok) {
        console.error('Meta API error details:', result.details);
        console.error('Creative spec:', JSON.stringify(result.creative_spec, null, 2));
        throw new Error(result.details || result.error || result.message || 'Failed to add creative to campaign');
      }

      // Dedup response — creative was already active, edge function skipped
      if (result.duplicate) {
        toast.dismiss('add-campaign');
        toast.success('Already added to Meta campaign');
        await logAction({
          entityType: 'creative',
          entityId: creative.id,
          action: 'add_creative',
          platform: 'meta',
          status: 'skipped',
          details: { reason: 'duplicate', existing_ad_id: result.existing_meta_ad_id },
        });
        setAddingToCampaign(null);
        return;
      }

      // Determine status based on Meta ad status
      const metaAdStatus = result.data?.status || 'ACTIVE';
      const creativeStatus = metaAdStatus === 'WITH_ISSUES' ? 'error' : 'active';

      // Update the ad creative with Meta campaign data in our database
      await supabase
        .from('ad_creatives' as any)
        .update({
          status: creativeStatus,
          meta_campaign_id: result.data?.campaign_id || campaign.campaign_id,
          meta_adset_id: result.data?.adset_id,
          meta_ad_id: result.data?.new_ad_id,
          meta_ad_status: metaAdStatus
        } as any)
        .eq('id' as any, creative.id as any);

      toast.dismiss('add-campaign');

      if (metaAdStatus === 'WITH_ISSUES') {
        toast.error(`Added but has issues - check Meta Ads Manager`);
      } else {
        toast.success(`Added as ad "${result.data?.ad_name}" to ${campaign.campaign_name}`);
      }

      await logAction({
        entityType: 'creative',
        entityId: creative.id,
        action: 'add_creative',
        platform: 'meta',
        status: metaAdStatus === 'WITH_ISSUES' ? 'error' : 'success',
        details: {
          campaign_id: result.data?.campaign_id || campaign.campaign_id,
          ad_id: result.data?.new_ad_id,
          ad_name: result.data?.ad_name,
          meta_ad_status: metaAdStatus,
        },
      });

      // Refresh ad creatives list to show updated status
      const filters = {
        artistId: urlParams.artist || artistId || selectedArtistId || undefined,
        platform: selectedPlatform || undefined,
        status: selectedStatus || undefined
      };
      fetchAdCreatives(adCreativesPagination.currentPage, adCreativesPagination.pageSize, filters, true);

    } catch (err) {
      toast.dismiss('add-campaign');
      console.error('Error adding to campaign:', err);
      toast.error(err instanceof Error ? err.message : 'Failed to add to campaign');
      await logAction({
        entityType: 'creative',
        entityId: creative.id,
        action: 'add_creative',
        platform: 'meta',
        status: 'error',
        details: { error: err instanceof Error ? err.message : String(err) },
      });
    } finally {
      setAddingToCampaign(null);
    }
  };

  // Add a single TikTok creative to the release's campaign.
  // Always throws on failure so bulk callers can count outcomes; `silent`
  // suppresses the per-creative toasts and list refresh during a bulk run.
  const handleAddToTikTokCampaign = async (creative: any, opts?: { silent?: boolean }) => {
    const silent = !!opts?.silent;
    if (!silent && addingToCampaign) return;

    setAddingToCampaign(creative.id);

    try {
      if (!creative.release_id) {
        throw new Error('Creative must be assigned to a release first');
      }

      const authCode = getTikTokAuthCode(creative);
      if (!authCode) {
        throw new Error('Creative has no TikTok auth code');
      }

      // Fetch release with TikTok campaign info and smartlink URL
      const { data: release, error: releaseError } = await supabase
        .from('releases')
        .select('*, artists(id, name), release_campaign_links(smartlink_url)')
        .eq('id', creative.release_id)
        .single();

      if (releaseError || !release) {
        throw new Error('Failed to fetch release info');
      }

      // Extract the smartlink URL linked to this release (from release_campaign_links)
      const rcLinkRaw = (release as any).release_campaign_links;
      const rcLink = Array.isArray(rcLinkRaw) ? rcLinkRaw[0] : rcLinkRaw;
      const smartlinkUrl: string | null = rcLink?.smartlink_url || null;

      const tiktokCampaignId = (release as any).tiktok_campaign_id;
      if (!tiktokCampaignId) {
        throw new Error('No TikTok campaign linked to this release');
      }

      // Linking an existing campaign only stores the campaign id, so resolve
      // the ad group from the live campaign when it is missing. We reuse the
      // existing ad group rather than creating a second one.
      let tiktokAdgroupId = (release as any).tiktok_adgroup_id;
      if (!tiktokAdgroupId) {
        const reconciled = await reconcileTikTokCampaign(creative.release_id, tiktokCampaignId);
        tiktokAdgroupId = reconciled.adgroupId;
      }
      if (!tiktokAdgroupId) {
        throw new Error(
          'TikTok campaign has no ad group yet — create the first ad in TikTok Ads Manager',
        );
      }

      if (!silent) toast.loading('Adding to TikTok campaign...', { id: 'add-tiktok' });

      // Build the UTM-preset landing page URL from the smartlink.
      // TikTok Smart+ WEB_CONVERSIONS ads require a landing_page_url —
      // without it the API rejects ad creation with "Please enter the
      // landing page URL." The TikTok placeholders (__CAMPAIGN_ID__, etc.)
      // are dynamically replaced by TikTok at delivery time.
      const landingPageUrl = smartlinkUrl
        ? `${smartlinkUrl}${smartlinkUrl.includes('?') ? '&' : '?'}utm_source=tiktok&utm_id=__CAMPAIGN_ID__&utm_campaign=__CAMPAIGN_NAME__&utm_medium=paid&utm_term=__AID__&utm_content=__CID__`
        : null;

      const response = await fetch(`${REPORTING_SUPABASE_URL}/functions/v1/add-creative-to-tiktok-campaign`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${REPORTING_SUPABASE_ANON_KEY}`,
        },
        body: JSON.stringify({
          campaign_id: tiktokCampaignId,
          adgroup_id: tiktokAdgroupId,
          tiktok_auth_code: authCode,
          creative_id: creative.id,
          creative_name: creative.videoName || `Creative ${creative.id}`,
          ...(landingPageUrl ? { landing_page_url: landingPageUrl } : {}),
        }),
      });

      const payload = await response.json().catch(() => ({} as any));

      // Dedup response — creative was already active, edge function skipped
      if (payload?.duplicate) {
        if (!silent) {
          toast.dismiss('add-tiktok');
          toast.success('Already added to TikTok campaign');
        }
        await logAction({
          entityType: 'creative',
          entityId: creative.id,
          action: 'add_creative',
          platform: 'tiktok',
          status: 'skipped',
          details: { reason: 'duplicate' },
        });
        setAddingToCampaign(null);
        return;
      }

      if (!response.ok || payload?.error) {
        // Non-video TikTok posts (CAROUSEL, IMAGE, …) can't be SINGLE_VIDEO
        // Spark Ads. Mark the creative as errored with a clear reason so it
        // stops showing as pending and the bulk-add doesn't keep retrying it.
        if (payload?.item_type && payload.item_type !== 'VIDEO') {
          await supabase
            .from('ad_creatives' as any)
            .update({
              status: 'error',
              rejection_reason: `TikTok post is ${payload.item_type}, not VIDEO — only video posts can be Spark Ads`,
              tiktok_ad_status: 'REJECTED',
            } as any)
            .eq('id' as any, creative.id as any);
          // Patch local state so the row updates without a full refetch.
          useStore.setState((state: any) => ({
            adCreatives: state.adCreatives.map((x: any) =>
              String(x.id) === String(creative.id)
                ? { ...x, status: 'error', rejectionReason: `TikTok post is ${payload.item_type}, not VIDEO — only video posts can be Spark Ads` }
                : x,
            ),
          }));
        }
        // Expired auth codes also can't be retried — mark as errored so
        // the bulk-add skips them instead of wasting API calls.
        if (payload?.expired) {
          await supabase
            .from('ad_creatives' as any)
            .update({
              status: 'error',
              rejection_reason: 'TikTok auth code has expired — creator must generate a new one',
              tiktok_ad_status: 'REJECTED',
            } as any)
            .eq('id' as any, creative.id as any);
          useStore.setState((state: any) => ({
            adCreatives: state.adCreatives.map((x: any) =>
              String(x.id) === String(creative.id)
                ? { ...x, status: 'error', rejectionReason: 'TikTok auth code has expired — creator must generate a new one' }
                : x,
            ),
          }));
        }
        throw new Error(payload?.details || payload?.error || 'Failed to add creative to TikTok campaign');
      }

      // Store the id of the ad that was just created, not the release's
      // pre-existing one — otherwise every creative ends up with the same
      // tiktok_ad_id and it is worthless as a de-duplication key.
      await supabase
        .from('ad_creatives' as any)
        .update({
          status: 'active',
          tiktok_campaign_id: tiktokCampaignId,
          tiktok_adgroup_id: tiktokAdgroupId,
          tiktok_ad_id: payload?.ad_id ?? null,
          tiktok_ad_status: 'ACTIVE'
        } as any)
        .eq('id' as any, creative.id as any);

      if (!silent) {
        toast.dismiss('add-tiktok');
        toast.success(`Added to TikTok campaign`);

        // Refresh ad creatives list
        const filters = {
          artistId: urlParams.artist || artistId || selectedArtistId || undefined,
          platform: selectedPlatform || undefined,
          status: selectedStatus || undefined
        };
        fetchAdCreatives(adCreativesPagination.currentPage, adCreativesPagination.pageSize, filters, true);
      }

      await logAction({
        entityType: 'creative',
        entityId: creative.id,
        action: 'add_creative',
        platform: 'tiktok',
        status: 'success',
        details: {
          campaign_id: tiktokCampaignId,
          ad_id: payload?.ad_id,
          creative_name: creative.videoName,
        },
      });
    } catch (err) {
      console.error('Error adding to TikTok campaign:', err);
      if (!silent) {
        toast.dismiss('add-tiktok');
        toast.error(err instanceof Error ? err.message : 'Failed to add to TikTok campaign');
      }
      await logAction({
        entityType: 'creative',
        entityId: creative.id,
        action: 'add_creative',
        platform: 'tiktok',
        status: 'error',
        details: { error: err instanceof Error ? err.message : String(err) },
      });
      throw err;
    } finally {
      // Without this the per-row button stays disabled forever after the
      // first add or error.
      setAddingToCampaign(null);
    }
  };

  // Bulk add all pending creatives for a release (fetches ALL creatives for this release, not just current page)
  const handleBulkAddToRelease = async (releaseId: string, _creatives: any[]) => {
    const abortController = new AbortController();
    setBulkAddAbortController(abortController);
    setBulkAddingRelease(releaseId);
    toast.loading('Loading all creatives for this release...', { id: 'bulk-add' });
    
    // Fetch every creative for this release (not just the current page), but
    // honour the active filters so we never add rows the user can't see.
    const { data: allCreatives, error } = await applyAdCreativeFilters(
      (supabase
        .from('ad_creatives' as any)
        .select('*, releases(id, name, meta_campaign_id, meta_campaign_name)') as any)
        .eq('release_id', releaseId),
      { platform: selectedPlatform, status: selectedStatus },
    ).order('created_at', { ascending: false });
    
    if (error || !allCreatives) {
      toast.dismiss('bulk-add');
      toast.error('Failed to load creatives for this release');
      setBulkAddingRelease(null);
      return;
    }
    
    // Transform to match expected format
    const transformedCreatives = allCreatives.map((c: any) => ({
      id: c.id,
      content: c.content,
      status: c.status,
      platform: c.platform,
      videoName: c.video_name,
      release_id: c.release_id,
      releases: c.releases,
      thumbnail_url: c.thumbnail_url,
      mergedInstagramReelUrl: c.merged_instagram_reel_url,
      original_instagram_url: c.original_instagram_url,
    }));
    
    // Same predicate the divider and the platform modal count with, so the
    // number the user was shown is the number we actually add.
    const pendingCreatives = transformedCreatives.filter(isMetaEligible);
    
    if (pendingCreatives.length === 0) {
      toast.dismiss('bulk-add');
      toast.error('No uploadable creatives found (only direct video uploads can be added to Meta)');
      setBulkAddingRelease(null);
      return;
    }

    toast.loading(`Adding ${pendingCreatives.length} creatives (rate limited: 1 per 10s, batches of 10)...`, { id: 'bulk-add' });

    let successCount = 0;
    let errorCount = 0;
    const BATCH_SIZE = 10;
    const DELAY_BETWEEN_CREATIVES = 10000; // 10 seconds

    // Process in batches of 10
    for (let batchStart = 0; batchStart < pendingCreatives.length; batchStart += BATCH_SIZE) {
      if (abortController.signal.aborted) {
        toast.dismiss('bulk-add');
        toast.success(`Stopped: Added ${successCount}/${pendingCreatives.length} creatives`);
        setBulkAddingRelease(null);
        setBulkAddAbortController(null);
        return;
      }
      
      const batch = pendingCreatives.slice(batchStart, batchStart + BATCH_SIZE);
      const batchNum = Math.floor(batchStart / BATCH_SIZE) + 1;
      const totalBatches = Math.ceil(pendingCreatives.length / BATCH_SIZE);
      
      for (let i = 0; i < batch.length; i++) {
        if (abortController.signal.aborted) {
          toast.dismiss('bulk-add');
          toast.success(`Stopped: Added ${successCount}/${pendingCreatives.length} creatives`);
          setBulkAddingRelease(null);
          setBulkAddAbortController(null);
          return;
        }
        
        const creative = batch[i];
        try {
          await handleAddToCampaign(creative);
          successCount++;
          const remaining = pendingCreatives.length - successCount - errorCount;
          const estimatedTime = remaining * 10;
          toast.loading(`Batch ${batchNum}/${totalBatches}: Added ${successCount}/${pendingCreatives.length} (~${Math.ceil(estimatedTime / 60)}min left)...`, { id: 'bulk-add' });
        } catch (err) {
          errorCount++;
          console.error('Error adding creative:', creative.id, err);
        }
        
        // Wait 10 seconds before next creative (except for the last one)
        if (i < batch.length - 1 || batchStart + BATCH_SIZE < pendingCreatives.length) {
          await new Promise(resolve => setTimeout(resolve, DELAY_BETWEEN_CREATIVES));
        }
      }
    }

    toast.dismiss('bulk-add');
    setBulkAddingRelease(null);
    setBulkAddAbortController(null);

    if (errorCount === 0) {
      toast.success(`Successfully added ${successCount} creatives to campaign`);
    } else {
      toast.error(`Added ${successCount} creatives, ${errorCount} failed`);
    }

    // Refresh the list
    const filters = {
      artistId: urlParams.artist || artistId || selectedArtistId || undefined,
      platform: selectedPlatform || undefined,
      status: selectedStatus || undefined
    };
    fetchAdCreatives(adCreativesPagination.currentPage, adCreativesPagination.pageSize, filters, true);
  };

  // Bulk add every eligible TikTok creative in a release to its linked
  // campaign. Reconciles against the live campaign first so creatives that
  // are already running are skipped instead of being added a second time.
  const handleBulkAddToTikTokCampaign = async (releaseId: string, campaignId: string) => {
    setBulkAddingTiktokRelease(releaseId);
    toast.loading('Checking what is already live on TikTok…', { id: 'bulk-add-tt' });

    let liveAuthCodes = new Set<string>();
    try {
      const reconciled = await reconcileTikTokCampaign(releaseId, campaignId);
      liveAuthCodes = reconciled.liveAuthCodes;
    } catch (err) {
      // Without a reliable picture of the campaign we could duplicate ads,
      // so refuse to continue rather than guess.
      console.error('TikTok reconcile failed', err);
      toast.dismiss('bulk-add-tt');
      toast.error(
        `Could not read the TikTok campaign, nothing was added: ${
          err instanceof Error ? err.message : 'unknown error'
        }`,
      );
      setBulkAddingTiktokRelease(null);
      return;
    }

    const { data: rows, error } = await applyAdCreativeFilters(
      supabase.from('ad_creatives').select('*').eq('release_id' as any, releaseId as any),
      { platform: selectedPlatform, status: selectedStatus },
    );
    if (error || !rows) {
      toast.dismiss('bulk-add-tt');
      toast.error('Failed to load release creatives');
      setBulkAddingTiktokRelease(null);
      return;
    }

    const pending = (rows as any[]).filter((c) => {
      if (!isTikTokEligible(c)) return false;
      // Second guard: the auth code is already a material in the campaign,
      // even if we failed to match it back to this row.
      const code = getTikTokAuthCode(c);
      return !!code && !liveAuthCodes.has(code);
    });

    if (pending.length === 0) {
      toast.dismiss('bulk-add-tt');
      toast.success('Nothing to add — every eligible creative is already live');
      setBulkAddingTiktokRelease(null);
      return;
    }

    toast.loading(`Adding ${pending.length} TikTok creative(s)…`, { id: 'bulk-add-tt' });
    let ok = 0;
    let fail = 0;
    for (const c of pending) {
      try {
        await handleAddToTikTokCampaign(
          { ...c, videoName: c.video_name },
          { silent: true },
        );
        ok += 1;
        toast.loading(`Added ${ok}/${pending.length} TikTok creative(s)…`, { id: 'bulk-add-tt' });
      } catch (err) {
        console.error('bulk TikTok add error for', c.id, err);
        fail += 1;
      }
      // Small pause to avoid hammering the edge function.
      await new Promise((r) => setTimeout(r, 400));
    }

    toast.dismiss('bulk-add-tt');
    setBulkAddingTiktokRelease(null);
    if (fail === 0) {
      toast.success(`Added ${ok} TikTok creative(s)`);
    } else {
      toast.error(`Added ${ok} TikTok creative(s), ${fail} failed`);
    }

    const filters = {
      artistId: urlParams.artist || artistId || selectedArtistId || undefined,
      platform: selectedPlatform || undefined,
      status: selectedStatus || undefined,
    };
    fetchAdCreatives(
      adCreativesPagination.currentPage,
      adCreativesPagination.pageSize,
      filters,
      true,
    );
  };

  // Helper function to check if Instagram URL is new (added after last activation)
  const isInstagramUrlNew = (creative: any): boolean => {
    if (!creative.instagram_url_updated_at || !creative.mergedInstagramReelUrl) return false;
    if (!creative.last_activated_at) return false;
    return new Date(creative.instagram_url_updated_at) > new Date(creative.last_activated_at);
  };

  // Helper function to check if TikTok code is new (added after last activation)
  const isTikTokCodeNew = (creative: any): boolean => {
    if (!creative.tiktok_url_updated_at || !creative.mergedTiktokAuthCode) return false;
    if (!creative.last_activated_at) return false;
    return new Date(creative.tiktok_url_updated_at) > new Date(creative.last_activated_at);
  };

  const handleCopyContent = async (creative: any) => {
    try {
      // If this is an Instagram URL and we don't have a thumbnail yet, try to fetch it
      if (creative.platform === 'instagram' && !creative.instagram_thumbnail_url) {
        try {
          console.log('Fetching Instagram thumbnail for URL:', creative.content);
          const result = await scanInstagramAudio(creative.id, creative.mergedInstagramReelUrl || creative.content, true);
          if (result?.thumbnail_url) {
            console.log('Instagram thumbnail fetched successfully');
            // Refresh to show the new thumbnail
            const filters = {
              artistId: urlParams.artist || artistId || selectedArtistId || undefined,
              platform: selectedPlatform || undefined,
              status: selectedStatus || undefined,
            };
            fetchAdCreatives(adCreativesPagination.currentPage, adCreativesPagination.pageSize, filters);
          }
        } catch (thumbnailError: any) {
          console.error('Error fetching Instagram thumbnail:', thumbnailError);
        }
      }
      
      await navigator.clipboard.writeText(creative.content);
      
      // If this is an Instagram URL and we don't have a thumbnail yet, try to fetch one
      if (creative.platform === 'instagram' && 
          !creative.instagram_thumbnail_url && 
          creative.content.includes('instagram.com')) {
        // This is now handled above
      }
      
      await updateAdCreativeStatus(creative.id, 'active');
      
      // Send WhatsApp notification if it's a Supabase storage URL
      if (isSupabaseStorageUrl(creative.content)) {
        try {
          const artistId = creative.artists_id;
          const artistName = getArtistName(artistId);
          const artistGroupId = getArtistWhatsAppGroupId(artistId);
          const videoName = creative.videoName || 'Video';
          
          // Import WhatsAppService dynamically to avoid circular dependencies
          const { WhatsAppService } = await import('../services/whatsapp');
          
          // Send notification
          await WhatsAppService.notifyAdCreativeUpdate({
            artistName,
            artistId,
            artistGroupId,
            platform: creative.platform,
            content: creative.content,
            status: 'active',
            videoName: videoName
          });
          
          console.log('✅ WhatsApp notification sent for ad creative approval');
        } catch (notifyError) {
          console.error('❌ Error sending WhatsApp notification:', notifyError);
        }
      }
      
      // Force re-render by updating a state that doesn't affect filtering
      setIsInitialized(prev => !prev);
    } catch (err: any) {
      console.error('Error copying content:', err);
    }
  };

  const handleDelete = async () => {
    if (deleteConfirmation.creativeId) {
      try {
        await deleteAdCreative(deleteConfirmation.creativeId);
      } catch (error: any) {
        console.error('Error deleting ad creative:', error);
      }
    }
    setDeleteConfirmation({ isOpen: false, creativeId: null });
  };

  const handleBulkDelete = async () => {
    if (parseInt(bulkDeleteInput) !== selectedCreatives.size) {
      return;
    }

    try {
      const count = selectedCreatives.size;

      // Delete all selected creatives
      await Promise.all(
        Array.from(selectedCreatives).map(id => deleteAdCreative(id))
      );

      // Clear selection and close modal
      setSelectedCreatives(new Set());
      setBulkDeleteConfirmation({ isOpen: false, count: 0 });
      setBulkDeleteInput('');

      // Re-fetch ad creatives to update the list
      const filters = {
        artistId: urlParams.artist || artistId || selectedArtistId || undefined,
        platform: selectedPlatform || undefined,
        status: selectedStatus || undefined
      };
      fetchAdCreatives(adCreativesPagination.currentPage, adCreativesPagination.pageSize, filters, true);
      
      toast.success(`${count} ad creative${count !== 1 ? 's' : ''} deleted`);
    } catch (error: any) {
      console.error('Error deleting ad creatives:', error);
      toast.error('Error deleting ad creatives');
    }
  };

  const handleRowClick = (creative: any, event: React.MouseEvent) => {
    // Don't open preview if clicking on buttons or links
    const target = event.target as HTMLElement;
    if (target.closest('button') || target.closest('a') || target.tagName === 'A' || target.closest('input')) {
      return;
    }

    // Open preview modal for any creative to show details
    // For video content (dropbox and direct_upload), show video player
    // For other platforms (instagram, tiktok), show the submitted content/URLs
    setPreviewModal({
      isOpen: true,
      videoUrl: (creative.platform === 'tiktok' || creative.content?.startsWith('#')) ? '' : creative.content, // Don't pass auth code for TikTok
      videoName: creative.videoName,
      platform: creative.platform,
      creativeId: creative.id
    });
  };

  const toggleSelectCreative = (id: string, index: number, shiftKey: boolean = false) => {
    const newSelected = new Set(selectedCreatives);
    
    if (shiftKey && lastSelectedIndex !== null) {
      // Shift-click: select range
      const start = Math.min(lastSelectedIndex, index);
      const end = Math.max(lastSelectedIndex, index);
      
      for (let i = start; i <= end; i++) {
        newSelected.add(filteredCreatives[i].id);
      }
    } else {
      // Normal click: toggle single item
      if (newSelected.has(id)) {
        newSelected.delete(id);
      } else {
        newSelected.add(id);
      }
    }
    
    setSelectedCreatives(newSelected);
    setLastSelectedIndex(index);
  };

  // The value worth copying for one creative: the TikTok Spark Ads auth code
  // (preferring the merged field, same precedence as getTikTokAuthCode), the
  // merged Instagram reel URL, or the raw content as a fallback for anything
  // else (e.g. a directly-pasted link).
  const getCopyValue = (creative: any): string | null => {
    if (creative.platform === 'tiktok') {
      return getTikTokAuthCode(creative) || creative.content || null;
    }
    if (creative.platform === 'instagram') {
      return creative.mergedInstagramReelUrl || creative.content || null;
    }
    return creative.content || null;
  };

  const handleBulkCopy = async () => {
    const values = filteredCreatives
      .filter((c) => selectedCreatives.has(c.id))
      .map(getCopyValue)
      .filter((v): v is string => !!v);

    if (values.length === 0) {
      toast.error('Nothing to copy in the current selection');
      return;
    }

    try {
      await navigator.clipboard.writeText(values.join('\n'));
      toast.success(`Copied ${values.length} link${values.length !== 1 ? 's' : ''}/code${values.length !== 1 ? 's' : ''}`);
    } catch (error) {
      console.error('Failed to copy selection:', error);
      toast.error('Failed to copy to clipboard');
    }
  };

  const handleBulkStatusChange = async (newStatus: string) => {
    if (selectedCreatives.size === 0) return;
    
    try {
      setIsBulkUpdatingStatus(true);
      setShowBulkStatusMenu(false);
      
      const ids = Array.from(selectedCreatives);
      
      // Update in batches of 100
      for (let i = 0; i < ids.length; i += 100) {
        const batch = ids.slice(i, i + 100);
        const { error } = await supabase
          .from('ad_creatives')
          .update({ status: newStatus, updated_at: new Date().toISOString() } as any)
          .in('id' as any, batch as any);
        
        if (error) throw error;
      }
      
      toast.success(`${ids.length} ad creative${ids.length > 1 ? 's' : ''} updated to ${newStatus}`);
      setSelectedCreatives(new Set());
      
      // Re-fetch with skipCache
      const filters = {
        artistId: urlParams.artist || artistId || selectedArtistId || undefined,
        platform: selectedPlatform || undefined,
        status: selectedStatus || undefined
      };
      fetchAdCreatives(adCreativesPagination.currentPage, adCreativesPagination.pageSize, filters, true);
    } catch (error) {
      console.error('Failed to bulk update ad creative status:', error);
      toast.error('Failed to update status');
    } finally {
      setIsBulkUpdatingStatus(false);
    }
  };

  const toggleSelectAll = async () => {
    if (selectedCreatives.size > 0) {
      setSelectedCreatives(new Set());
    } else {
      const effectiveArtistId = artistId || selectedArtistId || '';
      
      if (effectiveArtistId) {
        // Fetch ALL creative IDs for this artist from the database (not just current page)
        let query = supabase
          .from('ad_creatives')
          .select('id')
          .eq('artists_id' as any, effectiveArtistId as any);
        
        if (selectedPlatform) {
          query = query.eq('platform' as any, selectedPlatform as any);
        }
        if (selectedStatus) {
          query = query.eq('status' as any, selectedStatus as any);
        } else {
          query = query.neq('status' as any, 'archived' as any);
        }
        
        const { data: allCreatives, error } = await query;
        
        if (error) {
          console.error('Error fetching all creative IDs:', error);
          toast.error('Failed to select all creatives');
          return;
        }
        
        if (allCreatives) {
          const allIds = allCreatives.map((c: any) => c.id.toString());
          setSelectedCreatives(new Set(allIds));
        }
      } else {
        // No artist filter - select current page only
        setSelectedCreatives(new Set(filteredCreatives.map(c => c.id)));
      }
    }
  };

  const handleDetectDuplicates = async () => {
    const effectiveArtistId = artistId || selectedArtistId || '';
    
    if (!effectiveArtistId) {
      toast.error('Please select an artist first');
      return;
    }

    setIsDetectingDuplicates(true);
    try {
      // Always scan ALL videos for the artist, not just selected ones or current page
      const matches = await detectDuplicateCreatives(effectiveArtistId, undefined);
      setDuplicateMatches(matches);
      
      if (matches.length > 0) {
        toast.success(`Found ${matches.length} potential duplicate(s)`);
        setShowDuplicateModal(true);
      } else {
        toast.success('No duplicates found');
      }
    } catch (error) {
      console.error('Error detecting duplicates:', error);
      toast.error('Failed to detect duplicates');
    } finally {
      setIsDetectingDuplicates(false);
    }
  };

  const handleMergeCreatives = async (fileCreativeId: string, instagramCreativeId: string) => {
    console.log('🔄 Merge clicked - fileCreativeId:', fileCreativeId, 'instagramCreativeId:', instagramCreativeId);

    // Check if the target is actually a file upload
    const fileCreative = adCreatives.find(c => c.id === fileCreativeId);
    const instagramCreative = adCreatives.find(c => c.id === instagramCreativeId);

    if (!fileCreative || !instagramCreative) {
      toast.error('One or both creatives not found');
      return;
    }

    const isFileTarget = fileCreative.platform === 'direct_upload' || fileCreative.platform === 'dropbox';

    if (!isFileTarget) {
      // Handle Instagram + TikTok merge (update Instagram with TikTok info)
      const isInstagramTarget = fileCreative.platform === 'instagram';
      const isTikTokTarget = fileCreative.platform === 'tiktok';

      if (isInstagramTarget && (instagramCreative.platform === 'tiktok' || instagramCreative.mergedTiktokAuthCode)) {
        try {
          console.log('🔄 Merging TikTok into Instagram creative');

          // Update Instagram creative with TikTok information
          const tiktokCreative = instagramCreative;
          const igCreative = fileCreative;

          const updateData: any = {
            tiktok_campaign_id: (tiktokCreative as any).tiktok_campaign_id,
            merged_tiktok_auth_code: tiktokCreative.mergedTiktokAuthCode,
            tiktok_thumbnail_url: tiktokCreative.tiktok_thumbnail_url,
            tiktok_url_updated_at: new Date().toISOString(),
            updated_at: new Date().toISOString()
          };

          const { error } = await supabase
            .from('ad_creatives')
            .update(updateData)
            .eq('id' as any, igCreative.id as any);

          if (error) throw error;

          // Delete the TikTok creative
          await supabase
            .from('ad_creatives')
            .delete()
            .eq('id' as any, tiktokCreative.id as any);

          toast.success('TikTok merged into Instagram creative');

          // Refresh the list
          const filters = {
            artistId: urlParams.artist || artistId || selectedArtistId || undefined,
            platform: selectedPlatform || undefined,
            status: selectedStatus || undefined
          };
          await fetchAdCreatives(adCreativesPagination.currentPage, adCreativesPagination.pageSize, filters, true);

          // Remove from matches and close modal
          setDuplicateMatches([]);
          setShowDuplicateModal(false);
          return;
        } catch (error) {
          console.error('Error merging TikTok into Instagram:', error);
          toast.error('Failed to merge TikTok into Instagram');
          return;
        }
      } else if (isTikTokTarget && (instagramCreative.platform === 'instagram' || instagramCreative.mergedInstagramReelUrl)) {
        // Handle TikTok + Instagram merge (update TikTok with Instagram info)
        try {
          console.log('🔄 Merging Instagram into TikTok creative');

          const igCreative = instagramCreative;
          const tiktokCreative = fileCreative;

          const updateData: any = {
            merged_instagram_reel_url: igCreative.mergedInstagramReelUrl || igCreative.content,
            instagram_thumbnail_url: igCreative.instagram_thumbnail_url || igCreative.thumbnail_url,
            instagram_url_updated_at: new Date().toISOString(),
            updated_at: new Date().toISOString()
          };

          const { error } = await supabase
            .from('ad_creatives')
            .update(updateData)
            .eq('id' as any, tiktokCreative.id as any);

          if (error) throw error;

          // Delete the Instagram creative
          await supabase
            .from('ad_creatives')
            .delete()
            .eq('id' as any, igCreative.id as any);

          toast.success('Instagram merged into TikTok creative');

          // Refresh the list
          const filters = {
            artistId: urlParams.artist || artistId || selectedArtistId || undefined,
            platform: selectedPlatform || undefined,
            status: selectedStatus || undefined
          };
          await fetchAdCreatives(adCreativesPagination.currentPage, adCreativesPagination.pageSize, filters, true);

          // Remove from matches and close modal
          setDuplicateMatches([]);
          setShowDuplicateModal(false);
          return;
        } catch (error) {
          console.error('Error merging Instagram into TikTok:', error);
          toast.error('Failed to merge Instagram into TikTok');
          return;
        }
      } else {
        toast.error(`Merge requires a file upload (direct_upload or dropbox) as the target. Current target is ${fileCreative.platform}`);
        return;
      }
    }

    try {
      await mergeCreatives(String(fileCreativeId), String(instagramCreativeId));
      toast.success('Creatives merged successfully');
      
      // Refresh the list
      const filters = {
        artistId: urlParams.artist || artistId || selectedArtistId || undefined,
        platform: selectedPlatform || undefined,
        status: selectedStatus || undefined
      };
      fetchAdCreatives(adCreativesPagination.currentPage, adCreativesPagination.pageSize, filters, true);
      
      // Remove from matches
      setDuplicateMatches(prev => prev.filter(m => 
        m.fileCreative.id !== fileCreativeId && m.instagramCreative.id !== instagramCreativeId
      ));
      
      if (duplicateMatches.length <= 1) {
        setShowDuplicateModal(false);
      }
    } catch (error) {
      console.error('Error merging creatives:', error);
      toast.error('Failed to merge creatives');
    }
  };

  const handleBulkDeleteClick = () => {
    setBulkDeleteConfirmation({
      isOpen: true,
      count: selectedCreatives.size,
    });
    setBulkDeleteInput('');
  };

  const handleArchive = async (creative: { id: string; status: string }) => {
    try {
      await archiveAdCreative(creative.id);

      // Force a refresh to ensure the list updates with the new status
      const filters = {
        artistId: artistId || selectedArtistId || undefined,
        platform: selectedPlatform || undefined,
        status: selectedStatus || undefined
      };
      await fetchAdCreatives(adCreativesPagination.currentPage, adCreativesPagination.pageSize, filters, true);

      toast.success(creative.status === 'archived' ? 'Ad creative unarchived' : 'Ad creative archived');
    } catch (err: any) {
      console.error('Error archiving ad creative:', err);
      toast.error('Failed to archive ad creative: ' + (err.message || 'Unknown error'));
    }
  };

  const handleMergeSelected = async () => {
    const selectedIds = Array.from(selectedCreatives);
    const selectedCreativesData = adCreatives.filter(c => selectedIds.includes(c.id));

    if (selectedCreativesData.length < 2 || selectedCreativesData.length > 3) {
      toast.error('Please select 2-3 creatives to merge');
      return;
    }

    try {
      // Use duplicate detection on the selected creatives
      const artistId = selectedCreativesData[0].artists_id;
      const matches = await detectDuplicateCreatives(artistId, selectedIds);

      // Filter matches to only include the selected creatives
      const filteredMatches = matches.filter(match =>
        selectedIds.includes(match.fileCreative.id) && selectedIds.includes(match.instagramCreative.id)
      );

      setDuplicateMatches(filteredMatches);

      if (filteredMatches.length > 0) {
        toast.success(`Found ${filteredMatches.length} potential duplicate(s) among selected`);
        setShowDuplicateModal(true);
      } else {
        // If no duplicates found via detection, create manual match entries for the selected creatives
        const manualMatches: any[] = [];

        // Find the file creative (prefer direct_upload/dropbox)
        const fileCreative = selectedCreativesData.find(c => c.platform === 'direct_upload' || c.platform === 'dropbox');
        const nonFileCreatives = selectedCreativesData.filter(c => c.platform !== 'direct_upload' && c.platform !== 'dropbox');

        if (fileCreative && nonFileCreatives.length > 0) {
          // Create pairs: file creative with each non-file creative
          nonFileCreatives.forEach(nonFile => {
            manualMatches.push({
              fileCreative: fileCreative,
              instagramCreative: nonFile,
              similarity: 0 // Manual selection, no similarity score
            });
          });
        } else if (nonFileCreatives.length >= 2) {
          // No file creative, create pairs between non-file creatives (IG + TikTok, etc.)
          for (let i = 0; i < nonFileCreatives.length; i++) {
            for (let j = i + 1; j < nonFileCreatives.length; j++) {
              manualMatches.push({
                fileCreative: nonFileCreatives[i],
                instagramCreative: nonFileCreatives[j],
                similarity: 0
              });
            }
          }
        }

        if (manualMatches.length > 0) {
          setDuplicateMatches(manualMatches);
          toast.success(`Created ${manualMatches.length} manual merge pair(s)`);
          setShowDuplicateModal(true);
        } else {
          toast.error('No valid merge pairs found. Please select at least one file upload and one other creative.');
        }
      }
    } catch (error) {
      console.error('Error preparing merge:', error);
      toast.error('Failed to prepare merge');
    }
  };

  const handleMergeAll = async () => {
    if (duplicateMatches.length === 0) return;

    const fileCreativeId = duplicateMatches[0].fileCreative.id;
    const fileCreative = adCreatives.find(c => c.id === fileCreativeId);

    if (!fileCreative) {
      toast.error('File creative not found');
      return;
    }

    const isFileTarget = fileCreative.platform === 'direct_upload' || fileCreative.platform === 'dropbox';

    if (!isFileTarget) {
      toast.error('Merge All requires a file upload as the target');
      return;
    }

    try {
      // Merge all pairs
      for (const match of duplicateMatches) {
        const sourceCreative = match.instagramCreative;

        if (sourceCreative.platform === 'instagram') {
          // Merge Instagram into file
          await mergeCreatives(String(fileCreativeId), String(sourceCreative.id));
        } else if (sourceCreative.platform === 'tiktok') {
          // Merge TikTok into file
          const tiktokAuthCode = sourceCreative.merged_tiktok_auth_code || (typeof sourceCreative.content === 'string' && sourceCreative.content.startsWith('#') ? sourceCreative.content : null);
          console.log('🔍 Merging TikTok into file:', {
            sourceCreativeId: sourceCreative.id,
            sourcePlatform: sourceCreative.platform,
            sourceContent: sourceCreative.content,
            sourceMergedTiktok: sourceCreative.merged_tiktok_auth_code,
            extractedAuthCode: tiktokAuthCode
          });

          const updateData: any = {
            tiktok_campaign_id: sourceCreative.tiktok_campaign_id,
            merged_tiktok_auth_code: tiktokAuthCode,
            tiktok_thumbnail_url: sourceCreative.tiktok_thumbnail_url,
            tiktok_url_updated_at: new Date().toISOString(),
            updated_at: new Date().toISOString()
          };

          console.log('🔍 TikTok updateData:', updateData);

          const { error } = await supabase
            .from('ad_creatives')
            .update(updateData)
            .eq('id' as any, fileCreativeId as any);

          if (error) throw error;

          // Delete the TikTok creative
          await supabase
            .from('ad_creatives')
            .delete()
            .eq('id' as any, sourceCreative.id as any);
        }
      }

      toast.success(`Merged ${duplicateMatches.length} creatives into file upload`);

      // Refresh the list
      const filters = {
        artistId: urlParams.artist || artistId || selectedArtistId || undefined,
        platform: selectedPlatform || undefined,
        status: selectedStatus || undefined
      };
      await fetchAdCreatives(adCreativesPagination.currentPage, adCreativesPagination.pageSize, filters, true);

      // Fetch the updated file creative to verify the merge
      const { data: updatedCreative } = await supabase
        .from('ad_creatives')
        .select('*')
        .eq('id', fileCreativeId)
        .single();
      console.log('🔍 After merge, file creative data:', updatedCreative);

      // Clear selection and close modal
      setSelectedCreatives(new Set());
      setDuplicateMatches([]);
      setShowDuplicateModal(false);
    } catch (error) {
      console.error('Error merging all creatives:', error);
      toast.error('Failed to merge all creatives');
    }
  };

  

  const handleDownload = (creative: any) => {
    try {
      const url = creative.content;
      let fileName = creative.videoName || `video_${creative.id}.mov`;
      // Ensure the filename has a file extension — video_name in the DB
      // often lacks one (e.g. "Ad1_Tiffany Aris_Find Me_9-16_clean").
      if (fileName && !fileName.match(/\.[a-z0-9]{2,5}$/i)) {
        const ext = (url.split('?')[0].split('.').pop() || 'mp4').toLowerCase();
        if (ext.match(/^[a-z0-9]{2,5}$/)) fileName += `.${ext}`;
      }

      // Build a URL that forces the browser to download instead of opening.
      // - Supabase Storage: append ?download=filename → server sets
      //   Content-Disposition: attachment (streaming, no memory overhead).
      // - Dropbox: append ?dl=1 → direct download.
      // - Other URLs: fetch→blob as fallback (loads into memory, but works
      //   cross-origin where the download attribute is ignored).
      const downloadUrl = buildForcedDownloadUrl(url, fileName);

      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (err: any) {
      console.error('Error downloading file:', err);
      window.open(creative.content, '_blank', 'noopener,noreferrer');
    }
  };

  const shouldShowDownloadButton = (creative: any): boolean => {
    const url = creative.content;
    return isSupabaseStorageUrl(url) || url?.includes('dropbox.com');
  };

  const filteredCreatives = useMemo(() => {
    // Server-side filtering already handles artistId, platform, and status
    // We only need to apply client-side filtering for status exclusion when no status filter is set
    let result = adCreatives;

    // Only apply client-side status filter if it differs from server filter
    // Server already filters by status if selectedStatus is set
    // If no status is selected, server excludes archived, so no additional filtering needed

    // Hide pending TikTok creatives when their release's TikTok campaign is
    // DISABLE. There's no point showing them — they can't be added while the
    // campaign is paused. Active/error creatives are still shown so the user
    // can see what's already live or failed.
    result = result.filter((c: any) => {
      if (c.status !== 'pending') return true;
      if (!getTikTokAuthCode(c)) return true;
      const campaignStatus = (c as any).releases?.tiktok_campaign_status;
      return campaignStatus !== 'DISABLE';
    });

    // Hide pending creatives whose release hasn't come out yet (release_date
    // is in the future). Toggle with the "Unreleased" switch.
    if (!showUnreleased) {
      const now = new Date();
      result = result.filter((c: any) => {
        if (c.status !== 'pending') return true;
        const releaseDate = (c as any).releases?.release_date;
        if (!releaseDate) return true;
        return new Date(releaseDate) <= now;
      });
    }

    // Hide creatives belonging to content_only releases — these don't get
    // ads, so there's no reason to show them in the ad creative tab.
    result = result.filter((c: any) => {
      const pkg = (c as any).releases?.service_package;
      return pkg !== 'content_only';
    });

    return result;
  }, [adCreatives, showUnreleased]);

  // Determine if we're in artist view
  const isArtistView = !!artistId;

  // Collect content-only release names that were filtered out so we can
  // show a hint banner when an artist filter is active.
  const contentOnlyReleaseNames = useMemo(() => {
    if (!selectedArtistId && !artistId) return [];
    const seen = new Set<string>();
    const names: string[] = [];
    for (const c of adCreatives as any[]) {
      const rel = c.releases;
      if (rel?.service_package === 'content_only' && rel?.name && !seen.has(rel.id)) {
        seen.add(rel.id);
        names.push(rel.name);
      }
    }
    return names.sort();
  }, [adCreatives, selectedArtistId, artistId]);

  // Create a flat list with divider markers
  const creativesWithDividers = useMemo(() => {
    // In admin view, only show release dividers if artist filter is active
    const shouldShowReleaseDividers = isArtistView || selectedArtistId;
    
    if (!shouldShowReleaseDividers) {
      // No dividers - just return flat list (already sorted in filteredCreatives)
      return filteredCreatives.map(c => ({ type: 'creative' as const, data: c }));
    }
    
    // Artist view or artist filter active - show release dividers
    const releaseMap = new Map<string | null, any[]>();
    
    filteredCreatives.forEach(creative => {
      const releaseId = (creative as any).release_id || null;
      if (!releaseMap.has(releaseId)) {
        releaseMap.set(releaseId, []);
      }
      releaseMap.get(releaseId)!.push(creative);
    });

    // Check if we should show dividers
    const shouldShowDividers = releaseMap.size > 1 || (releaseMap.size === 1 && !releaseMap.has(null));
    
    if (!shouldShowDividers) {
      return filteredCreatives.map(c => ({ type: 'creative' as const, data: c }));
    }

    // Create sorted groups
    const groups: { releaseId: string | null; releaseName: string | null; releaseDate: string | null; creatives: any[] }[] = [];
    releaseMap.forEach((creatives, releaseId) => {
      groups.push({
        releaseId,
        releaseName: releaseId ? (creatives[0] as any).releases?.name : null,
        releaseDate: releaseId ? (creatives[0] as any).releases?.release_date : null,
        creatives
      });
    });

    // Sort: releases first by release_date descending (newest first), then "No Release"
    groups.sort((a, b) => {
      if (a.releaseId === null) return 1;
      if (b.releaseId === null) return -1;
      const aTime = a.releaseDate ? new Date(a.releaseDate).getTime() : 0;
      const bTime = b.releaseDate ? new Date(b.releaseDate).getTime() : 0;
      if (bTime !== aTime) return bTime - aTime;
      return (a.releaseName || '').localeCompare(b.releaseName || '');
    });

    // Flatten with dividers
    const result: Array<{ type: 'divider' | 'creative'; data: any; releaseName?: string }> = [];
    groups.forEach(group => {
      result.push({ type: 'divider', data: group, releaseName: group.releaseName || 'No Release' });
      group.creatives.forEach(creative => {
        result.push({ type: 'creative', data: creative });
      });
    });

    return result;
  }, [filteredCreatives, isArtistView, selectedArtistId]);

  // Check if any filter is active (required for bulk selection)
  const hasActiveFilter = !!(selectedArtistId || selectedPlatform || selectedStatus);

  // Check for videos missing thumbnails
  useEffect(() => {
    const checkMissingThumbnails = async () => {
      const count = await getVideosMissingThumbnails(artistId || selectedArtistId || undefined);
      setVideosMissingThumbnails(count);
    };
    checkMissingThumbnails();
  }, [adCreatives, artistId, selectedArtistId]);

  const handleBulkAudioScan = async () => {
    if (isScanningAudio) return;
    setIsScanningAudio(true);
    toast.loading('Scanning Instagram reels for licensed audio...', { id: 'audio-scan' });
    try {
      const result = await scanAllInstagramAudio(100);
      toast.dismiss('audio-scan');
      if (!result) {
        toast.error('Audio scan failed');
        return;
      }
      if (result.total === 0) {
        toast.success('No unscanned Instagram reels found');
      } else {
        toast.success(
          `Scanned ${result.total}: ${result.licensed} licensed (${result.downloaded} downloaded), ${result.original} original, ${result.errors} errors`,
          { duration: 6000 }
        );
      }
      // Refresh list to reflect content/URL updates
      fetchAdCreatives(
        adCreativesPagination.currentPage,
        adCreativesPagination.pageSize,
        {
          artistId: artistId || selectedArtistId || undefined,
          platform: selectedPlatform || undefined,
          status: selectedStatus || undefined,
        }
      ).catch(console.error);
    } catch (err) {
      toast.dismiss('audio-scan');
      console.error('Audio scan error:', err);
      toast.error('Audio scan failed');
    } finally {
      setIsScanningAudio(false);
    }
  };

  const handleBulkThumbnailGeneration = async () => {
    const controller = new AbortController();
    setBulkThumbnailAbortController(controller);
    setShowThumbnailModal(true);

    try {
      const result = await generateBulkThumbnails({
        artistId: artistId || selectedArtistId || undefined,
        onProgress: setBulkThumbnailProgress,
        signal: controller.signal
      });

      if (result.success) {
        toast.success(result.message);
        // Refresh the list
        const filters = {
          artistId: artistId || selectedArtistId || undefined,
          platform: selectedPlatform || undefined,
          status: selectedStatus || undefined
        };
        fetchAdCreatives(adCreativesPagination.currentPage, adCreativesPagination.pageSize, filters);
        // Update missing count
        const count = await getVideosMissingThumbnails(artistId || selectedArtistId || undefined);
        setVideosMissingThumbnails(count);
      } else {
        toast.error(result.message);
      }
    } catch (error) {
      console.error('Bulk thumbnail generation error:', error);
      toast.error('Failed to generate thumbnails');
    } finally {
      setBulkThumbnailAbortController(null);
      setTimeout(() => {
        setShowThumbnailModal(false);
        setBulkThumbnailProgress(null);
      }, 2000);
    }
  };

  return (
    <div className="p-3 sm:p-6 space-y-4 sm:space-y-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
        <div>
          <h2 className="text-lg sm:text-2xl font-semibold dark:text-white">
            {isArtistView ? `${getArtistName(artistId)}'s Ad Creatives` : 'Ad Creatives'} ({adCreativesPagination.totalCount})
          </h2>
          <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
            Manage Instagram Reels and TikTok URLs
          </p>
        </div>
        <div className="flex gap-2 flex-wrap">
          {/* {videosMissingThumbnails > 0 && (
            <button 
              onClick={handleBulkThumbnailGeneration}
              disabled={!!bulkThumbnailAbortController}
              className="btn bg-purple-600 hover:bg-purple-700 text-white"
              title={`Generate thumbnails for ${videosMissingThumbnails} videos`}
            >
              <Image className="h-5 w-5 mr-2" />
              Generate Thumbnails ({videosMissingThumbnails})
            </button>
          )}
          <button
            onClick={handleBulkAudioScan}
            disabled={isScanningAudio}
            className="btn bg-amber-600 hover:bg-amber-700 text-white disabled:opacity-60"
            title="Scan submitted Instagram reels for licensed audio; download licensed videos to storage"
          >
            <AlertCircle className="h-5 w-5 mr-2" />
            {isScanningAudio ? 'Scanning audio…' : 'Scan IG audio'}
          </button> */}
          {!isArtistView && (urlParams.artist || artistId || selectedArtistId) && (
            <Link
              to={`/artist/${urlParams.artist || artistId || selectedArtistId}/ad-creatives`}
              target="_blank"
              rel="noopener noreferrer"
              className="btn bg-gray-800 hover:bg-gray-900 text-white"
              title="Open this artist's view in a new tab"
            >
              <ExternalLink className="h-5 w-5 mr-2" />
              Open Artist View
            </Link>
          )}
          <button onClick={() => setShowSubmissionModal(true)} className="btn">
            <Plus className="h-5 w-5 mr-2" />
            Add URLs
          </button>
        </div>
      </div>

      {/* Only show filter menu in admin view */}
      {!isArtistView && (
        <AdCreativesFilterMenu
          selectedArtist={selectedArtistId || ''}
          setSelectedArtist={(id) => {
            // Log a stack trace whenever the artist filter is cleared so we
            // can see who's triggering it (input onChange, X button,
            // "All Artists", clearAllFilters, ...).
            if (!id) {
              console.warn(
                '🎨 setSelectedArtist called with EMPTY — clearing artist filter. Stack:',
                new Error('artist-filter-cleared').stack,
              );
            } else {
              console.log('🎨 setSelectedArtist called with:', id);
            }
            setSelectedArtistId(id || null);
            updateFilterUrl('artist', id);
            setAdCreativesPage(1); // Reset to page 1 when filter changes
          }}
          selectedPlatform={selectedPlatform}
          setSelectedPlatform={(platform) => {
            setSelectedPlatform(platform);
            updateFilterUrl('platform', platform);
            setAdCreativesPage(1); // Reset to page 1 when filter changes
          }}
          selectedStatus={selectedStatus}
          setSelectedStatus={(status) => {
            setSelectedStatus(status);
            updateFilterUrl('status', status);
            setAdCreativesPage(1); // Reset to page 1 when filter changes
          }}
        />
      )}

      {/* Toggle for unreleased creatives */}
      {!isArtistView && (
        <div className="flex items-center gap-2 mb-2">
          <button
            onClick={() => setShowUnreleased(!showUnreleased)}
            className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${
              showUnreleased ? 'bg-primary-600' : 'bg-gray-300 dark:bg-gray-600'
            }`}
          >
            <span
              className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform ${
                showUnreleased ? 'translate-x-[18px]' : 'translate-x-[3px]'
              }`}
            />
          </button>
          <span className="text-xs text-gray-500 dark:text-gray-400">
            Unreleased anzeigen
          </span>
        </div>
      )}

      {/* Content-only hint when artist filter is active */}
      {contentOnlyReleaseNames.length > 0 && (selectedArtistId || artistId) && (
        <div className="mb-2 px-3 py-2 bg-gray-100 dark:bg-gray-700/50 border border-gray-200 dark:border-gray-600 rounded-lg flex items-center gap-2">
          <Info className="h-3.5 w-3.5 text-gray-400 flex-shrink-0" />
          <span className="text-xs text-gray-500 dark:text-gray-400">
            Content Only — nicht angezeigt: {contentOnlyReleaseNames.join(', ')}
          </span>
        </div>
      )}

      <div className="bg-white dark:bg-gray-800 shadow rounded-lg overflow-hidden">
        {/* Bulk actions bar */}
        {selectedCreatives.size > 0 && (
          <div className="bg-primary-50 dark:bg-primary-900/20 border-b border-primary-200 dark:border-primary-800 px-3 sm:px-6 py-3 flex items-center justify-between">
            <span className="text-sm font-medium text-primary-900 dark:text-primary-100">
              {selectedCreatives.size > 0 
                ? `${selectedCreatives.size} ad creative${selectedCreatives.size !== 1 ? 's' : ''} selected`
                : 'Artist selected'
              }
            </span>
            <div className="flex items-center gap-1.5">
              {selectedCreatives.size > 0 && (
                <button
                  onClick={() => setShowBulkReleaseModal(true)}
                  className="flex items-center gap-1.5 px-2 py-1 bg-primary-600 hover:bg-primary-700 text-white rounded-md transition-colors text-xs font-medium"
                >
                  <Edit className="h-3.5 w-3.5" />
                  Assign
                </button>
              )}
              {selectedCreatives.size >= 2 && selectedCreatives.size <= 3 ? (
                <button
                  onClick={handleMergeSelected}
                  className="flex items-center gap-1.5 px-2 py-1 bg-primary-600 hover:bg-primary-700 text-white rounded-md transition-colors text-xs font-medium"
                >
                  <GitMerge className="h-3.5 w-3.5" />
                  Merge
                </button>
              ) : (
                <button
                  onClick={handleDetectDuplicates}
                  disabled={isDetectingDuplicates}
                  className="flex items-center gap-1.5 px-2 py-1 bg-primary-600 hover:bg-primary-700 text-white rounded-md transition-colors text-xs font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isDetectingDuplicates ? <Loader className="h-3.5 w-3.5 animate-spin" /> : <Search className="h-3.5 w-3.5" />}
                  Duplicates
                </button>
              )}
              {/* Copies every selected creative's auth code / reel link as a
                  plain list, one per line — for pasting into Meta/TikTok or
                  wherever the links are actually needed in bulk. */}
              {selectedCreatives.size > 0 && (
                <button
                  onClick={handleBulkCopy}
                  className="flex items-center gap-1.5 px-2 py-1 bg-primary-600 hover:bg-primary-700 text-white rounded-md transition-colors text-xs font-medium"
                  title="Copy all auth codes / reel links in the selection, one per line"
                >
                  <Copy className="h-3.5 w-3.5" />
                  Copy
                </button>
              )}
              {/* Bulk-download every selected creative that has (or can
                  have) a stored video file — packed into a single zip.
                  Resolves missing TikTok mirrors on the fly. */}
              {selectedCreatives.size > 0 && (
                <button
                  onClick={handleBulkDownloadVideos}
                  disabled={!!bulkDownloadProgress}
                  className="flex items-center gap-1.5 px-2 py-1 bg-primary-600 hover:bg-primary-700 text-white rounded-md transition-colors text-xs font-medium disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
                  title="Download every stored video file in the selection (zip)"
                >
                  {bulkDownloadProgress ? (
                    <Loader className="h-3.5 w-3.5 animate-spin" />
                  ) : (
                    <Download className="h-3.5 w-3.5" />
                  )}
                  {bulkDownloadProgress
                    ? `${bulkDownloadProgress.done}/${bulkDownloadProgress.total}`
                    : 'Download'}
                </button>
              )}
              <div className="relative">
                <button
                  onClick={() => setShowBulkStatusMenu(!showBulkStatusMenu)}
                  disabled={isBulkUpdatingStatus}
                  className="flex items-center gap-1.5 px-2 py-1 bg-primary-600 hover:bg-primary-700 text-white rounded-md transition-colors text-xs font-medium"
                >
                  {isBulkUpdatingStatus ? <Loader className="h-3.5 w-3.5 animate-spin" /> : <AlertCircle className="h-3.5 w-3.5" />}
                  Status
                </button>
                {showBulkStatusMenu && (
                  <div className="absolute top-full right-0 mt-1 bg-gray-800 border border-gray-700 rounded-lg shadow-lg z-20 min-w-[180px]">
                    {[
                      { value: 'pending', label: 'Pending' },
                      { value: 'active', label: 'Active' },
                      { value: 'error', label: 'Error' },
                      { value: 'archived', label: 'Archived' },
                    ].map((status) => (
                      <button
                        key={status.value}
                        onClick={() => handleBulkStatusChange(status.value)}
                        className="w-full text-left px-4 py-2 text-sm text-white hover:bg-gray-700 transition-colors first:rounded-t-lg last:rounded-b-lg"
                      >
                        {status.label}
                      </button>
                    ))}
                  </div>
                )}
              </div>
              <button
                onClick={handleBulkDeleteClick}
                className="flex items-center gap-1.5 px-2 py-1 bg-red-600 hover:bg-red-700 text-white rounded-md transition-colors text-xs font-medium"
              >
                <Trash2 className="h-3.5 w-3.5" />
                Delete
              </button>
            </div>
          </div>
        )}
        <div 
          className="overflow-x-auto"
          onMouseEnter={() => setIsHoveringTable(true)}
          onMouseLeave={() => setIsHoveringTable(false)}
        >
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead className="bg-gray-50 dark:bg-gray-700">
              <tr>
                {!isArtistView && hasActiveFilter && (
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider w-12">
                    <input
                      type="checkbox"
                      checked={selectedCreatives.size > 0}
                      onChange={toggleSelectAll}
                      className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded cursor-pointer"
                    />
                  </th>
                )}
                {!isArtistView && (
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Artist
                  </th>
                )}
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Thumbnail
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Platform
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Content
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Submitted
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
              {loading ? (
                <tr>
                  <td colSpan={isArtistView ? 6 : (hasActiveFilter ? 8 : 7)} className="px-6 py-4 text-center text-gray-500 dark:text-gray-400">
                    Loading...
                  </td>
                </tr>
              ) : filteredCreatives.length === 0 ? (
                <tr>
                  <td colSpan={isArtistView ? 6 : (hasActiveFilter ? 8 : 7)} className="px-6 py-4 text-center text-gray-500 dark:text-gray-400">
                    {(selectedArtistId || selectedPlatform || selectedStatus) ? 'No ad creatives match the selected filters' : 'No ad creatives found'}
                  </td>
                </tr>
              ) : (
                creativesWithDividers.map((item, index) => {
                  if (item.type === 'divider') {
                    const releaseData = item.data;
                    const releasesObj = releaseData?.creatives?.[0]?.releases as any;
                    const hasMetaCampaign = releasesObj?.meta_campaign_id;
                    const metaCampaignId = releasesObj?.meta_campaign_id;
                    const metaCampaignName = releasesObj?.meta_campaign_name;
                    const hasTikTokCampaign = releasesObj?.tiktok_campaign_id;
                    const tiktokCampaignId = releasesObj?.tiktok_campaign_id;
                    const tiktokCampaignName = releasesObj?.tiktok_campaign_name;
                    // ENABLE / DISABLE / null (not yet fetched). When DISABLE,
                    // pending TikTok creatives for this release are hidden —
                    // there's no point showing them if the campaign can't
                    // receive new ads.
                    const tiktokCampaignStatus: string | null = releasesObj?.tiktok_campaign_status ?? null;
                    const tiktokCampaignDisabled = hasTikTokCampaign && tiktokCampaignStatus === 'DISABLE';
                    // release_campaign_links is embedded in the store query.
                    // PostgREST returns this as a single object (not an
                    // array) because release_id is UNIQUE — it detects the
                    // 1:1 relationship. Defensive: handle both shapes in
                    // case the FK introspection ever changes.
                    const rcLinkRaw = releasesObj?.release_campaign_links;
                    const rcLink = Array.isArray(rcLinkRaw) ? rcLinkRaw[0] : rcLinkRaw;
                    const currentSmartlinkUrl: string | null = rcLink?.smartlink_url || null;
                    const currentSmartlinkId: string | null = rcLink?.smartlink_id || null;
                    const currentReportId: string | null = rcLink?.report_id || null;
                    const hasSmartlink = !!currentSmartlinkUrl;
                    const hasReport = !!currentReportId;
                    const releaseArtistName: string | null =
                      releaseData?.creatives?.[0]?.artists_id
                        ? getArtistName(releaseData.creatives[0].artists_id)
                        : null;
                    const releaseArtistId: string | null = releaseData?.creatives?.[0]?.artists_id || null;
                    
                    // Counts for the rows currently visible. These are a
                    // preview only — the modal recounts against the DB after
                    // reconciling with the live TikTok campaign.
                    const pendingCount = releaseData.creatives.filter(isMetaEligible).length;
                    const pendingTiktokCount = releaseData.creatives.filter(isTikTokEligible).length;
                    const pendingAnyCount = releaseData.creatives.filter(
                      (c: any) => isMetaEligible(c) || isTikTokEligible(c),
                    ).length;
                    
                    return (
                      <tr key={`divider-${index}`}>
                        {/* Wide cell: covers everything up to (but not
                            including) Status. We dropped 2 from the
                            original colSpan to make room for the song.so
                            and Report cells below. */}
                        <td colSpan={isArtistView ? 3 : (hasActiveFilter ? 5 : 4)} className="px-6 py-2" style={{ backgroundColor: '#0000fe' }}>
                          <div className="flex items-center gap-2">
                            {!isArtistView && hasActiveFilter && (() => {
                              const releaseId: string | null = releaseData.releaseId;
                              const visibleIds: string[] = releaseData.creatives.map((c: any) => String(c.id));
                              // We treat the checkbox as "checked" when every
                              // id we know about for this release is in the
                              // selection \u2014 either the cached full list (if
                              // we've already fetched it) or just the rows
                              // visible on this page.
                              const knownIds: string[] = releaseId
                                ? releaseIdsCache.current.get(releaseId) || visibleIds
                                : visibleIds;
                              const allSelected =
                                knownIds.length > 0 &&
                                knownIds.every((id: string) => selectedCreatives.has(id));
                              const isLoading =
                                !!releaseId && loadingReleaseSelect === releaseId;
                              const handleToggle = async (checked: boolean) => {
                                // Without a release_id (the "No Release"
                                // bucket) we fall back to the visible page
                                // \u2014 there's no good way to query by null
                                // release_id alongside the active filters.
                                if (!releaseId) {
                                  const next = new Set(selectedCreatives);
                                  visibleIds.forEach((id) =>
                                    checked ? next.add(id) : next.delete(id),
                                  );
                                  setSelectedCreatives(next);
                                  return;
                                }
                                let allIds = releaseIdsCache.current.get(releaseId);
                                if (!allIds) {
                                  setLoadingReleaseSelect(releaseId);
                                  try {
                                    let q = supabase
                                      .from('ad_creatives')
                                      .select('id')
                                      .eq('release_id' as any, releaseId as any);
                                    // Mirror the active server-side filters so
                                    // selecting "this release" really only
                                    // selects rows that match what the user
                                    // is currently viewing.
                                    if (selectedStatus) {
                                      q = q.eq('status' as any, selectedStatus as any);
                                    } else {
                                      q = q.neq('status' as any, 'archived' as any);
                                    }
                                    if (selectedPlatform === 'instagram') {
                                      q = q.or(
                                        'platform.eq.instagram,merged_instagram_reel_url.not.is.null' as any,
                                      );
                                    } else if (selectedPlatform === 'tiktok') {
                                      q = q.or(
                                        'platform.eq.tiktok,merged_tiktok_auth_code.not.is.null' as any,
                                      );
                                    } else if (selectedPlatform) {
                                      q = q.eq('platform' as any, selectedPlatform as any);
                                    }
                                    const { data, error } = await q;
                                    if (error) throw error;
                                    allIds = (data || []).map((r: any) => String(r.id));
                                    releaseIdsCache.current.set(releaseId, allIds);
                                  } catch (err) {
                                    console.error('release-select fetch failed', err);
                                    toast.error('Failed to load full release');
                                    setLoadingReleaseSelect(null);
                                    return;
                                  }
                                  setLoadingReleaseSelect(null);
                                }
                                const next = new Set(selectedCreatives);
                                allIds!.forEach((id) =>
                                  checked ? next.add(id) : next.delete(id),
                                );
                                setSelectedCreatives(next);
                              };
                              return isLoading ? (
                                <Loader className="h-4 w-4 animate-spin text-white" />
                              ) : (
                                <input
                                  type="checkbox"
                                  checked={allSelected}
                                  onChange={(e) => {
                                    void handleToggle(e.target.checked);
                                  }}
                                  className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-white/50 rounded cursor-pointer"
                                />
                              );
                            })()}
                            {releaseData.releaseId ? (
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  const rel = releaseData.creatives?.[0]?.releases;
                                  setAssetRelease({
                                    id: releaseData.releaseId,
                                    name: item.releaseName,
                                    release_date: rel?.release_date,
                                    cover_url: rel?.cover_url,
                                    spotify_uri: rel?.spotify_uri,
                                  });
                                }}
                                className="text-sm font-medium text-white hover:underline decoration-dotted underline-offset-4 cursor-pointer"
                                title="Show cover & Spotify URI"
                              >
                                {item.releaseName}
                              </button>
                            ) : (
                              <span className="text-sm font-medium text-white">
                                {item.releaseName === 'No Release' ? 'No Release Assigned' : item.releaseName}
                              </span>
                            )}
                            {/* Campaign / smartlink pills only make sense for
                                 a real release row — the "No Release" bucket
                                 has releaseId === null and any update would
                                 silently match 0 rows. */}
                            {!!releaseData.releaseId && (
                            <>
                            {/* Meta campaign pill — always rendered. Colored
                                 blue + white when this release is linked to
                                 a Meta campaign, greyscale+transparent
                                 otherwise. Click (any state) opens the
                                 platform-aware LinkReleaseCampaignModal so
                                 the user can see the linked campaign, unlink
                                 it, or search for a different one. */}
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                setLinkingCampaign({
                                  platform: 'meta',
                                  releaseId: releaseData.releaseId,
                                  releaseName: item.releaseName || null,
                                  currentCampaignId: metaCampaignId || null,
                                  currentCampaignName: metaCampaignName || null,
                                });
                              }}
                              className={
                                'flex items-center gap-1 px-2 py-0.5 rounded text-xs transition-colors ' +
                                (hasMetaCampaign
                                  ? 'bg-blue-600 hover:bg-blue-700 text-white'
                                  : 'bg-transparent border border-white/30 text-white/40 grayscale hover:bg-white/10')
                              }
                              title={
                                hasMetaCampaign
                                  ? `Linked Meta Campaign: ${metaCampaignName || metaCampaignId} — click to manage`
                                  : 'No Meta campaign linked — click to link'
                              }
                            >
                              <svg className="h-3 w-3" viewBox="0 0 24 24" fill="currentColor">
                                <path d="M12 2.04C6.5 2.04 2 6.53 2 12.06C2 17.06 5.66 21.21 10.44 21.96V14.96H7.9V12.06H10.44V9.85C10.44 7.34 11.93 5.96 14.22 5.96C15.31 5.96 16.45 6.15 16.45 6.15V8.62H15.19C13.95 8.62 13.56 9.39 13.56 10.18V12.06H16.34L15.89 14.96H13.56V21.96A10 10 0 0 0 22 12.06C22 6.53 17.5 2.04 12 2.04Z" />
                              </svg>
                              <span className="hidden sm:inline">Meta</span>
                            </button>
                            {/* TikTok campaign pill — same behaviour as Meta. */}
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                setLinkingCampaign({
                                  platform: 'tiktok',
                                  releaseId: releaseData.releaseId,
                                  releaseName: item.releaseName || null,
                                  currentCampaignId: tiktokCampaignId || null,
                                  currentCampaignName: tiktokCampaignName || null,
                                });
                              }}
                              className={
                                'flex items-center gap-1 px-2 py-0.5 rounded text-xs transition-colors ' +
                                (hasTikTokCampaign
                                  ? tiktokCampaignDisabled
                                    ? 'bg-pink-600/40 text-white/60 cursor-help'
                                    : 'bg-pink-600 hover:bg-pink-700 text-white'
                                  : 'bg-transparent border border-white/30 text-white/40 grayscale hover:bg-white/10')
                              }
                              title={
                                hasTikTokCampaign
                                  ? `Linked TikTok Campaign: ${tiktokCampaignName || tiktokCampaignId}${tiktokCampaignDisabled ? ' (DISABLED — pending creatives hidden)' : ''} — click to manage`
                                  : 'No TikTok campaign linked — click to link'
                              }
                            >
                              <svg className="h-3 w-3" viewBox="0 0 24 24" fill="currentColor">
                                <path d="M12.525.02c1.31-.02 2.61-.01 3.91-.02.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.08 1.4-.54 2.79-1.35 3.94-1.31 1.92-3.58 3.17-5.91 3.21-1.49.01-2.98-.32-4.22-1.25-1.24-.93-2.1-2.38-2.23-3.89-.12-1.35.13-2.74.87-3.88.74-1.15 1.92-1.96 3.19-2.4 1.25-.43 2.58-.48 3.85-.15V.02z" />
                              </svg>
                              <span className="hidden sm:inline">TikTok</span>
                            </button>
                            </>
                            )}
                          </div>
                        </td>
                        {/* Status column → small black song.so pill.
                             50% opacity if no smartlink linked, 100% if
                             linked. Click opens search/link/unlink modal. */}
                        <td className="px-6 py-2" style={{ backgroundColor: '#0000fe' }}>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setLinkingSmartlink({
                                releaseId: releaseData.releaseId,
                                releaseName: item.releaseName || null,
                                artistName: releaseArtistName,
                                currentSmartlinkUrl,
                                currentSmartlinkId,
                              });
                            }}
                            className={
                              'flex items-center gap-1 px-2 py-0.5 rounded text-xs transition-colors bg-black hover:bg-gray-900 text-white ' +
                              (hasSmartlink ? 'opacity-100' : 'opacity-50')
                            }
                            title={
                              hasSmartlink
                                ? `Linked smartlink: ${currentSmartlinkUrl} — click to manage`
                                : 'No smartlink linked — click to search song.so'
                            }
                          >
                            {hasSmartlink && <Check className="h-3 w-3" />}
                            <span className="hidden sm:inline">song.so</span>
                          </button>
                        </td>
                        {/* Submitted column → small dark grey Report pill. */}
                        <td className="px-6 py-2" style={{ backgroundColor: '#0000fe' }}>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setLinkingReport({
                                releaseId: releaseData.releaseId,
                                releaseName: item.releaseName || null,
                                artistName: releaseArtistName,
                                artistId: releaseArtistId,
                                currentReportId,
                              });
                            }}
                            className={
                              'flex items-center gap-1 px-2 py-0.5 rounded text-xs transition-colors bg-gray-700 hover:bg-gray-800 text-white ' +
                              (hasReport ? 'opacity-100' : 'opacity-50')
                            }
                            title={
                              hasReport
                                ? `Linked report: ${currentReportId} — click to manage`
                                : 'No report linked — click to search and link a report'
                            }
                          >
                            {hasReport && <Check className="h-3 w-3" />}
                            <span className="hidden sm:inline">Report</span>
                          </button>
                        </td>
                        <td className="px-6 py-2" style={{ backgroundColor: '#0000fe' }}>
                          <div className="flex items-center gap-2">
                            {/* No platform filter → one button that opens the
                                 platform-choice modal, so the user picks
                                 Meta or TikTok before anything is added.
                                 With a platform filter active the intent is
                                 already unambiguous, so the direct
                                 per-platform buttons below are used. */}
                            {!selectedPlatform && pendingAnyCount > 0 && (
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setBulkAddPlatformFor({
                                    releaseId: releaseData.releaseId,
                                    releaseName: item.releaseName || null,
                                    metaCampaignId: metaCampaignId || null,
                                    tiktokCampaignId: tiktokCampaignId || null,
                                  });
                                }}
                                disabled={
                                  bulkAddingRelease === releaseData.releaseId ||
                                  bulkAddingTiktokRelease === releaseData.releaseId
                                }
                                className="flex items-center gap-1 px-2 py-0.5 bg-green-600 hover:bg-green-700 rounded text-xs text-white transition-colors disabled:opacity-50"
                                title="Choose a platform, then add this release's pending creatives"
                              >
                                {bulkAddingRelease === releaseData.releaseId ||
                                bulkAddingTiktokRelease === releaseData.releaseId ? (
                                  <Loader className="h-3 w-3 animate-spin" />
                                ) : (
                                  <PlusCircle className="h-3 w-3" />
                                )}
                                <span className="hidden sm:inline">Add {pendingAnyCount}…</span>
                              </button>
                            )}
                            {!!selectedPlatform && hasMetaCampaign && pendingCount > 0 && (
                              <>
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleBulkAddToRelease(releaseData.releaseId, releaseData.creatives);
                                  }}
                                  disabled={bulkAddingRelease === releaseData.releaseId}
                                  className="flex items-center gap-1 px-2 py-0.5 bg-green-600 hover:bg-green-700 rounded text-xs text-white transition-colors disabled:opacity-50"
                                  title={`Add all ${pendingCount} pending creatives to campaign`}
                                >
                                  {bulkAddingRelease === releaseData.releaseId ? (
                                    <Loader className="h-3 w-3 animate-spin" />
                                  ) : (
                                    <PlusCircle className="h-3 w-3" />
                                  )}
                                  <span className="hidden sm:inline">Add {pendingCount}</span>
                                </button>
                                {bulkAddingRelease === releaseData.releaseId && bulkAddAbortController && (
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      bulkAddAbortController.abort();
                                    }}
                                    className="flex items-center gap-1 px-2 py-0.5 bg-red-600 hover:bg-red-700 rounded text-xs text-white transition-colors"
                                    title="Stop bulk add"
                                  >
                                    <X className="h-3 w-3" />
                                    <span className="hidden sm:inline">Stop</span>
                                  </button>
                                )}
                              </>
                            )}
                            {/* Pink TikTok bulk-add button — shown when a
                                 TikTok campaign is linked to this release
                                 and there are auth-code creatives without a
                                 tiktok_ad_id yet. Sits inline with the
                                 green Meta button. */}
                            {!!selectedPlatform && hasTikTokCampaign && pendingTiktokCount > 0 && (
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleBulkAddToTikTokCampaign(releaseData.releaseId, tiktokCampaignId);
                                }}
                                disabled={bulkAddingTiktokRelease === releaseData.releaseId}
                                className="flex items-center gap-1 px-2 py-0.5 bg-pink-600 hover:bg-pink-700 rounded text-xs text-white transition-colors disabled:opacity-50"
                                title={`Add all ${pendingTiktokCount} pending TikTok auth code(s) to campaign`}
                              >
                                {bulkAddingTiktokRelease === releaseData.releaseId ? (
                                  <Loader className="h-3 w-3 animate-spin" />
                                ) : (
                                  <PlusCircle className="h-3 w-3" />
                                )}
                                <span className="hidden sm:inline">Add {pendingTiktokCount}</span>
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  }
                  
                  const creative = item.data;
                  return (
                  <tr 
                    key={creative.id} 
                    className={`hover:bg-gray-50 dark:hover:bg-gray-700 ${
                      (creative.platform === 'dropbox' || creative.platform === 'direct_upload') 
                        ? 'cursor-pointer' 
                        : ''
                    } ${
                      selectedCreatives.has(creative.id) ? 'bg-primary-50 dark:bg-primary-900/10' : ''
                    }`}
                    onClick={(e) => handleRowClick(creative, e)}
                  >
                    {!isArtistView && hasActiveFilter && (
                      <td 
                        className="px-6 py-4 whitespace-nowrap w-12"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <input
                          type="checkbox"
                          checked={selectedCreatives.has(creative.id)}
                          onChange={() => {}}
                          onClick={(e) => {
                            const index = filteredCreatives.findIndex(c => c.id === creative.id);
                            toggleSelectCreative(creative.id, index, e.shiftKey);
                          }}
                          className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded cursor-pointer"
                        />
                      </td>
                    )}
                    {!isArtistView && (
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900 dark:text-gray-100">
                          {getArtistName(creative.artists_id)}
                        </div>
                      </td>
                    )}
                    {/* Thumbnail column — image preview with fallback chain:
                         thumbnail_url → instagram_thumbnail_url → tiktok
                         poster (stored → source → preview → thumbnail).
                         Falls back to a blank placeholder if none available. */}
                    <td className="px-6 py-4 whitespace-nowrap">
                      {(() => {
                        const thumbSrc =
                          creative.thumbnail_url ||
                          creative.instagram_thumbnail_url ||
                          creative.tiktok_post_poster_stored_url ||
                          creative.tiktok_post_poster_url ||
                          creative.tiktok_post_preview_url ||
                          creative.tiktok_thumbnail_url ||
                          null;
                        if (thumbSrc) {
                          return (
                            <div className="relative w-12 h-20 bg-gray-800 rounded overflow-hidden">
                              <img
                                src={thumbSrc}
                                alt="Creative thumbnail"
                                className="w-full h-full object-cover"
                              />
                            </div>
                          );
                        }
                        return (
                          <div className="w-12 h-20 bg-gray-200 dark:bg-gray-700 rounded" />
                        );
                      })()}
                    </td>
                    {/* Platform column — up to 3 icons shown in parallel
                         (File, Instagram, TikTok) based on what sources the
                         creative actually carries. */}
                    <td className="px-6 py-4 whitespace-nowrap">
                      {(() => {
                        const content = creative.content || '';
                        const hasFile =
                          (content.includes('://') && isSupabaseStorageUrl(content)) ||
                          content.includes('dropbox.com');
                        const hasInstagram =
                          !!creative.mergedInstagramReelUrl ||
                          !!creative.original_instagram_url ||
                          content.includes('instagram.com');
                        const hasTiktok =
                          !!creative.mergedTiktokAuthCode ||
                          (typeof content === 'string' && content.startsWith('#'));
                        return (
                          <div className="flex items-center gap-2">
                            {hasFile && (
                              <HardDrive className="h-5 w-5 text-white" aria-label="Stored video file" />
                            )}
                            {hasInstagram && (
                              <Instagram className="h-5 w-5 text-white" aria-label="Instagram" />
                            )}
                            {hasTiktok && (
                              <svg viewBox="0 0 24 24" fill="currentColor" className="h-5 w-5 text-white" aria-label="TikTok">
                                <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z" />
                              </svg>
                            )}
                          </div>
                        );
                      })()}
                    </td>
                    <td className="px-6 py-4">
                      <div className="space-y-1">
                        {creative.videoName ? (
                          <div>
                            <div className="flex items-center gap-2 mb-1 flex-wrap">
                              <span className="font-semibold text-gray-900 dark:text-gray-100 mb-1">
                                {creative.videoName}
                              </span>
                              {creative.is_trial_reel && (
                                <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wide font-medium px-1.5 py-0.5 rounded text-purple-600 bg-purple-50 dark:bg-purple-900/20 dark:text-purple-400" title="This reel is a Trial Reel — only shared to non-followers, not visible in profile grid">
                                  Trial Reel
                                </span>
                              )}
                              {creative.ig_author_username && (
                                <a
                                  href={`https://instagram.com/${creative.ig_author_username}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-[10px] text-gray-500 dark:text-gray-400 hover:text-primary-600 dark:hover:text-primary-400"
                                >
                                  @{creative.ig_author_username}
                                </a>
                              )}
                            </div>
                            {/* Post date + caption (thumbnail and URL shown elsewhere) */}
                            {!(creative.original_instagram_url && creative.mergedInstagramReelUrl === creative.content) && (
                              (creative.ig_post_date || creative.ig_caption) && (
                                <div className="mt-1 ml-4">
                                  {creative.ig_post_date && (
                                    <span className="block text-[10px] text-gray-400 dark:text-gray-500">
                                      {new Date(creative.ig_post_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                                    </span>
                                  )}
                                  {creative.ig_caption && (
                                    <span className="block text-[10px] text-gray-400 dark:text-gray-500 mt-0.5 line-clamp-2 max-w-[200px] sm:max-w-[250px]">
                                      {creative.ig_caption}
                                    </span>
                                  )}
                                </div>
                              )
                            )}

                            {/* Display merged Instagram URL if available and different from content (or licensed-replaced) */}
                            {creative.mergedInstagramReelUrl && 
                             creative.mergedInstagramReelUrl !== creative.content && 
                             !creative.mergedInstagramReelUrl.startsWith('#') &&
                             (creative.mergedInstagramReelUrl !== creative.content || creative.original_instagram_url) && (
                              <div className="flex items-center gap-1 mt-1 ml-4">
                                <span className="text-gray-400">↳</span>
                                {isInstagramUrlNew(creative) && (
                                  <div className="w-2 h-2 rounded-full bg-orange-500" title="New content added" />
                                )}
                                <Instagram
                                  className="h-4 w-4 text-white"
                                />
                                <div className="relative group">
                                  <a
                                    href={creative.mergedInstagramReelUrl}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="hover:underline truncate block max-w-[150px] sm:max-w-[200px] md:max-w-[250px] text-xs text-gray-500 dark:text-gray-400"
                                    title={creative.mergedInstagramReelUrl}
                                  >
                                    {creative.mergedInstagramReelUrl}
                                  </a>
                                  
                                  {/* Instagram thumbnail preview on hover */}
                                  <div className="absolute left-0 bottom-full mb-2 hidden group-hover:block z-10">
                                    {creative.instagram_thumbnail_url ? (
                                      <div className="bg-gray-900 rounded-md p-1 shadow-lg">
                                        <img 
                                          src={creative.instagram_thumbnail_url} 
                                          alt="Instagram thumbnail" 
                                          className="w-32 h-auto rounded"
                                        />
                                      </div>
                                    ) : (
                                      <div className="bg-gray-900 text-gray-200 rounded-md p-2 shadow-lg text-xs">
                                        No thumbnail available
                                      </div>
                                    )}
                                  </div>
                                </div>
                              </div>
                            )}

                            {/* Original Instagram URL (preserved when licensed audio was replaced) — link only, no label */}
                            {creative.original_instagram_url && (
                              <div className="flex items-center gap-1 mt-1 ml-4">
                                <span className="text-gray-400">↳</span>
                                <a
                                  href={creative.original_instagram_url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="hover:underline truncate block max-w-[150px] sm:max-w-[200px] md:max-w-[250px] text-xs text-gray-500 dark:text-gray-400"
                                  title={creative.original_instagram_url}
                                >
                                  {creative.original_instagram_url}
                                </a>
                              </div>
                            )}


                            {/* Display merged TikTok auth code if available */}
                            {creative.mergedTiktokAuthCode && (
                              <div className="flex items-center gap-1 mt-1 ml-4">
                                <span className="text-gray-400">↳</span>
                                {isTikTokCodeNew(creative) && (
                                  <div className="w-2 h-2 rounded-full bg-orange-500" title="New content added" />
                                )}
                                <svg 
                                  viewBox="0 0 24 24" 
                                  fill="currentColor"
                                  className="h-4 w-4 text-white"
                                >
                                  <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
                                </svg>
                                <span className="truncate block max-w-[150px] sm:max-w-[200px] md:max-w-[250px] text-xs text-gray-500 dark:text-gray-400" title={creative.mergedTiktokAuthCode}>
                                  {creative.mergedTiktokAuthCode}
                                </span>
                              </div>
                            )}
                          </div>
                        ) : (
                          creative.platform !== 'tiktok' ? (
                            <div className="flex items-center gap-2">
                              {creative.original_instagram_url ? (
                                <>
                                  <Instagram className="h-4 w-4 text-white" />
                                  <a
                                    href={creative.original_instagram_url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="hover:underline truncate block max-w-[150px] sm:max-w-[200px] md:max-w-[250px]"
                                    title={creative.original_instagram_url}
                                  >
                                    {creative.original_instagram_url}
                                  </a>
                                </>
                              ) : (
                              <a
                                href={creative.content}
                                target="_blank" 
                                rel="noopener noreferrer" 
                                className={`hover:underline truncate block max-w-[150px] sm:max-w-[200px] md:max-w-[250px] ${
                                  creative.platform === 'instagram' ? 'relative group' : ''
                                }`}
                                title={creative.content}
                              >
                                {creative.content}
                                
                                {/* Instagram thumbnail preview on hover */}
                                {creative.platform === 'instagram' && (
                                  <div className="absolute left-0 bottom-full mb-2 hidden group-hover:block z-10">
                                    {creative.instagram_thumbnail_url ? (
                                      <div className="bg-gray-900 rounded-md p-1 shadow-lg">
                                        <img 
                                          src={creative.instagram_thumbnail_url} 
                                          alt="Instagram thumbnail" 
                                          className="w-32 h-auto rounded"
                                        />
                                      </div>
                                    ) : (
                                      <div className="bg-gray-900 text-gray-200 rounded-md p-2 shadow-lg text-xs">
                                        No thumbnail available
                                      </div>
                                    )}
                                  </div>
                                )}
                              </a>
                              )}
                            </div>
                          ) : (
                            <div className="flex items-center gap-2">
                              <span className="font-mono text-sm truncate block max-w-[150px] sm:max-w-[200px] md:max-w-[250px]" title={creative.content}>
                                {creative.content}
                              </span>
                            </div>
                          )
                        )}

                        {/* Audio-type badge — only show "Original sound" (licensed label hidden) */}
                        {creative.platform === 'instagram' && !creative.original_instagram_url && creative.audio_type && creative.audio_type !== 'licensed_music' && (
                          <div className="mt-1 ml-4 relative group/license inline-flex">
                            <span
                              className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wide font-medium px-1.5 py-0.5 rounded cursor-help text-green-600/80 bg-green-50 dark:bg-green-900/20"
                            >
                              <Info className="h-3 w-3" />
                              Original sound
                            </span>
                            <div className="absolute left-0 top-full mt-1 hidden group-hover/license:block z-20 w-64 bg-gray-900 text-gray-100 text-xs leading-snug rounded-md p-2 shadow-lg">
                              This Reel uses an original sound and is eligible to be advertised as an Instagram post.
                            </div>
                          </div>
                        )}

                        {/* Replaced storage file (when licensed audio replaced the reel) — link only, no label */}
                        {creative.original_instagram_url && !creative.videoName && (
                          <div className="mt-1 ml-4">
                            <div className="flex items-center gap-1">
                              <span className="text-gray-400">↳</span>
                              <a
                                href={creative.content}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="hover:underline truncate block max-w-[150px] sm:max-w-[200px] md:max-w-[250px] text-xs text-gray-500 dark:text-gray-400"
                                title={creative.content}
                              >
                                {creative.content}
                              </a>
                            </div>
                          </div>
                        )}

                        {/* Display merged Instagram URL if available and different from content (skip for licensed-replaced) */}
                        {creative.mergedInstagramReelUrl && creative.mergedInstagramReelUrl !== creative.content && !creative.videoName && !creative.original_instagram_url && (
                          <div className="flex items-center gap-1 mt-1 ml-4">
                            <span className="text-gray-400">↳</span>
                            {isInstagramUrlNew(creative) && (
                              <div className="w-2 h-2 rounded-full bg-orange-500" title="New content added" />
                            )}
                            <Instagram
                              className="h-4 w-4 text-white"
                            />
                            <a
                              href={creative.mergedInstagramReelUrl}
                              target="_blank"
                              rel="noopener noreferrer" 
                              className="hover:underline truncate block max-w-[150px] sm:max-w-[200px] md:max-w-[250px] text-xs text-gray-500 dark:text-gray-400 relative group"
                              title={creative.mergedInstagramReelUrl}
                            >
                              {creative.mergedInstagramReelUrl}
                              
                              {/* Instagram thumbnail preview on hover */}
                              <div className="absolute left-0 bottom-full mb-2 hidden group-hover:block z-10">
                                {creative.instagram_thumbnail_url ? (
                                  <div className="bg-gray-900 rounded-md p-1 shadow-lg">
                                    <img 
                                      src={creative.instagram_thumbnail_url} 
                                      alt="Instagram thumbnail" 
                                      className="w-32 h-auto rounded"
                                    />
                                  </div>
                                ) : (
                                  <div className="bg-gray-900 text-gray-200 rounded-md p-2 shadow-lg text-xs">
                                    No thumbnail available
                                  </div>
                                )}
                              </div>
                            </a>
                          </div>
                        )}
                        
                        {/* Display merged TikTok auth code if available */}
                        {creative.mergedTiktokAuthCode && !creative.videoName && (
                          <div className="flex items-center gap-1 mt-1 ml-4">
                            <span className="text-gray-400">↳</span>
                            {isTikTokCodeNew(creative) && (
                              <div className="w-2 h-2 rounded-full bg-orange-500" title="New content added" />
                            )}
                            <svg 
                              viewBox="0 0 24 24" 
                              fill="currentColor"
                              className="h-4 w-4 text-white"
                            >
                              <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
                            </svg>
                            <span className="truncate block max-w-[150px] sm:max-w-[200px] md:max-w-[250px] text-xs text-gray-500 dark:text-gray-400" title={creative.mergedTiktokAuthCode}>
                              {creative.mergedTiktokAuthCode}
                            </span>
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {(() => {
                        const displayStatus = getDisplayStatus(creative);
                        return (
                          <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusBadgeClass(displayStatus)}`}>
                            {displayStatus.charAt(0).toUpperCase() + displayStatus.slice(1)}
                          </span>
                        );
                      })()}
                      {creative.status === 'rejected' && creative.rejectionReason && (
                        <RejectionReasonTag reason={creative.rejectionReason} />
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-500 dark:text-gray-400">
                        {format(new Date(creative.createdAt), 'dd/MM/yy, HH:mm')}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center space-x-3">
                        <button
                          onClick={() => handleCopyContent(creative)}
                          className="text-white hover:text-gray-300 transition-colors"
                          title="Copy Content"
                        >
                          <Copy className="h-5 w-5" />
                        </button>
                        {shouldShowDownloadButton(creative) && (
                          <button
                            onClick={() => handleDownload(creative)}
                            className="text-white hover:text-gray-300 transition-colors"
                            title="Download Video"
                          >
                            <Download className="h-5 w-5" />
                          </button>
                        )}
                        {/* TikTok-only creatives (platform=tiktok, no IG/file
                             content) skip the Meta branch entirely — only
                             the TikTok action icon is rendered below. */}
                        {(() => {
                          const isTikTokOnly =
                            creative.platform === 'tiktok' &&
                            !creative.mergedInstagramReelUrl &&
                            !isSupabaseStorageUrl(creative.content);
                          if (isTikTokOnly) return null;
                          if (creative.meta_ad_status === 'WITH_ISSUES') {
                            return (
                              <button
                                onClick={() => setReaddConfirmation({ isOpen: true, creative })}
                                className="text-red-500 hover:text-red-600 dark:text-red-400 dark:hover:text-red-300 transition-colors"
                                title={`Meta Ad Error - Click to re-add (Ad ID: ${creative.meta_ad_id})`}
                              >
                                <AlertCircle className="h-5 w-5" />
                              </button>
                            );
                          }
                          if (creative.status === 'active' || creative.status === 'error') {
                            return (
                              <button
                                onClick={() => setReaddConfirmation({ isOpen: true, creative })}
                                className="text-white hover:text-gray-300 transition-colors"
                                title={`Added to Campaign - Click to re-add (Ad ID: ${creative.meta_ad_id})`}
                              >
                                <CheckCircle2 className="h-5 w-5" />
                              </button>
                            );
                          }
                          return (
                            <button
                              onClick={() => handleAddToCampaign(creative)}
                              disabled={addingToCampaign === creative.id}
                              className="text-blue-500 hover:text-blue-600 dark:text-blue-400 dark:hover:text-blue-300 transition-colors disabled:opacity-50"
                              title="Add to Meta Campaign"
                            >
                              {addingToCampaign === creative.id ? (
                                <Loader className="h-5 w-5 animate-spin" />
                              ) : (
                                <svg className="h-5 w-5" viewBox="0 0 24 24" fill="currentColor">
                                  <path d="M12 2.04c-5.5 0-10 4.49-10 10.02 0 5 3.66 9.15 8.44 9.9v-7H7.9v-2.9h2.54V9.85c0-2.51 1.49-3.89 3.78-3.89 1.09 0 2.23.19 2.23.19v2.47h-1.26c-1.24 0-1.63.77-1.63 1.56v1.88h2.78l-.45 2.9h-2.33v7a10 10 0 0 0 8.44-9.9c0-5.53-4.5-10.02-10-10.02Z" />
                                </svg>
                              )}
                            </button>
                          );
                        })()}
                        {/* TikTok action icon — shown whenever the creative
                             carries an auth code, regardless of Meta state.
                             Positioned here so for TikTok-only creatives
                             (where the Meta branch above returns null) this
                             ends up immediately left of the Edit button. */}
                        {!!getTikTokAuthCode(creative) && (
                          <button
                            onClick={() => { void handleAddToTikTokCampaign(creative).catch(() => {}); }}
                            disabled={addingToCampaign === creative.id}
                            className="text-gray-900 hover:text-black dark:text-gray-100 dark:hover:text-white transition-colors disabled:opacity-50"
                            title="Add to TikTok Campaign"
                          >
                            {addingToCampaign === creative.id ? (
                              <Loader className="h-5 w-5 animate-spin" />
                            ) : (
                              <svg className="h-5 w-5" viewBox="0 0 24 24" fill="currentColor">
                                <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z" />
                              </svg>
                            )}
                          </button>
                        )}
                        {/* Download button — visible for any creative whose
                            video file is (or can be) stored in our buckets:
                              • Direct Supabase uploads (content URL).
                              • TikTok Spark Ads (resolves
                                tiktok_post_video_stored_url on demand via
                                download-tiktok-spark-ad-media if missing).
                            Instagram-only rows have no permanent video and
                            therefore don't render this button. */}
                        {(() => {
                          const isDirectStored = isSupabaseStorageUrl(
                            creative.content || '',
                          );
                          const isTikTok =
                            creative.platform === 'tiktok' ||
                            !!creative.mergedTiktokAuthCode;
                          if (!isDirectStored && !isTikTok) return null;
                          const isBusy =
                            downloadingId === String(creative.id);
                          return (
                            <button
                              onClick={() => handleDownloadCreative(creative)}
                              disabled={isBusy}
                              className="text-white hover:text-gray-300 transition-colors disabled:opacity-50"
                              title="Download video file"
                            >
                              {isBusy ? (
                                <Loader className="h-5 w-5 animate-spin" />
                              ) : (
                                <Download className="h-5 w-5" />
                              )}
                            </button>
                          );
                        })()}
                        <button
                          onClick={() => setEditingCreative(creative)}
                          className="text-white hover:text-gray-300 transition-colors"
                          title="Edit Content"
                        >
                          <Edit className="h-5 w-5" />
                        </button>
                        <button
                          onClick={() => handleArchive(creative)}
                          className="text-red-600 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300 transition-colors"
                          title={creative.status === 'archived' ? 'Unarchive' : 'Archive'}
                        >
                          <Archive className="h-5 w-5" />
                        </button>
                        <button
                          onClick={() => setDeleteConfirmation({
                            isOpen: true,
                            creativeId: creative.id
                          })}
                          className="text-red-600 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300 transition-colors"
                          title="Delete"
                        >
                          <Trash2 className="h-5 w-5" />
                        </button>
                      </div>
                    </td>
                  </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
        {!isArtistView && (
          <Pagination
            currentPage={adCreativesPagination.currentPage}
            totalPages={adCreativesPagination.totalPages}
            totalCount={adCreativesPagination.totalCount}
            pageSize={adCreativesPagination.pageSize}
            onPageChange={(page) => {
              setAdCreativesPage(page);
              // Build filters object for pagination
              const filters = {
                artistId: artistId || selectedArtistId || undefined,
                platform: selectedPlatform || undefined,
                status: selectedStatus || undefined
              };
              fetchAdCreatives(page, adCreativesPagination.pageSize, filters);
            }}
            loading={loading}
          />
        )}
      </div>

      {showSubmissionModal && modalInitialized && (
        <AdCreativeSubmissionModal
          isOpen={showSubmissionModal}
          onClose={() => {
            setShowSubmissionModal(false);
            // Re-fetch with current filters to ensure release dividers appear
            const filters = {
              artistId: artistId || selectedArtistId || undefined,
              platform: selectedPlatform || undefined,
              status: selectedStatus || undefined
            };
            fetchAdCreatives(adCreativesPagination.currentPage, adCreativesPagination.pageSize, filters, true);
          }}
          artistId={selectedArtistId || undefined}
        />
      )}

      {editingCreative && (
        <EditAdCreativeModal
          creative={editingCreative}
          onClose={() => setEditingCreative(null)}
        />
      )}

      <ConfirmationModal
        isOpen={deleteConfirmation.isOpen}
        title="Delete Ad Creative"
        message="Are you sure you want to delete this ad creative? This action cannot be undone."
        onConfirm={handleDelete}
        onCancel={() => setDeleteConfirmation({ isOpen: false, creativeId: null })}
      />

      {/* Bulk delete confirmation modal */}
      {bulkDeleteConfirmation.isOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              Delete Multiple Ad Creatives
            </h3>
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
              You are about to delete <strong>{bulkDeleteConfirmation.count}</strong> ad creative{bulkDeleteConfirmation.count !== 1 ? 's' : ''}. This action cannot be undone.
            </p>
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
              To confirm, please enter the number of ad creatives you want to delete:
            </p>
            <input
              type="number"
              value={bulkDeleteInput}
              onChange={(e) => setBulkDeleteInput(e.target.value)}
              placeholder={`Enter ${bulkDeleteConfirmation.count}`}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 dark:bg-gray-700 dark:text-white mb-4"
              autoFocus
            />
            <div className="flex justify-end gap-3">
              <button
                onClick={() => {
                  setBulkDeleteConfirmation({ isOpen: false, count: 0 });
                  setBulkDeleteInput('');
                }}
                className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-md transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleBulkDelete}
                disabled={parseInt(bulkDeleteInput) !== bulkDeleteConfirmation.count}
                className="px-4 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Delete {bulkDeleteConfirmation.count} Ad Creative{bulkDeleteConfirmation.count !== 1 ? 's' : ''}
              </button>
            </div>
          </div>
        </div>
      )}

      <VideoPreviewModal
        isOpen={previewModal.isOpen}
        onClose={() => setPreviewModal({ ...previewModal, isOpen: false })}
        videoUrl={previewModal.videoUrl}
        videoName={previewModal.videoName}
        platform={previewModal.platform}
        creativeId={previewModal.creativeId}
      />

      {/* Duplicate detection modal */}
      {showDuplicateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-4xl w-full mx-4 max-h-[90vh] overflow-hidden flex flex-col">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                {duplicateMatches.some(m => m.similarity > 0) ? 'Potential Duplicate Creatives' : 'Merge Creatives'}
              </h3>
              <button
                onClick={() => setShowDuplicateModal(false)}
                className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
              >
                <X className="h-6 w-6" />
              </button>
            </div>
            
            {duplicateMatches.length === 0 ? (
              <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                No duplicate creatives found
              </div>
            ) : (
              <div>
                {/* Show Merge All button if there are multiple pairs with same target */}
                {duplicateMatches.length > 1 && (() => {
                  const hasCommonTarget = duplicateMatches.length > 1 &&
                    duplicateMatches.every(m => m.fileCreative.id === duplicateMatches[0].fileCreative.id);
                  const isFileTarget = duplicateMatches[0].fileCreative.platform === 'direct_upload' || duplicateMatches[0].fileCreative.platform === 'dropbox';
                  if (hasCommonTarget && isFileTarget) {
                    return (
                      <div className="mb-4 p-3 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded">
                        <button
                          onClick={handleMergeAll}
                          className="w-full px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-md transition-colors text-sm font-medium"
                        >
                          Merge All ({duplicateMatches.length} pairs)
                        </button>
                        <p className="text-xs text-green-800 dark:text-green-200 mt-2">
                          Merges all creatives into the file upload: {duplicateMatches[0].fileCreative.video_name || duplicateMatches[0].fileCreative.content?.split('/').pop() || 'Unnamed'}
                        </p>
                      </div>
                    );
                  }
                  return null;
                })()}

                <div className="overflow-y-auto flex-1">
                  <div className="space-y-4">
                    {duplicateMatches.map((match, index) => (
                      <div key={index} className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
                        <div className="flex items-center justify-between mb-4">
                          <div className="text-sm font-medium text-gray-900 dark:text-white">
                            {match.similarity > 0
                              ? `Similarity: ${(match.similarity * 100).toFixed(1)}%`
                              : 'Manual selection'
                            }
                          </div>
                          <button
                            onClick={() => handleMergeCreatives(match.fileCreative.id, match.instagramCreative.id)}
                            className="flex items-center gap-2 px-3 py-1.5 bg-primary-600 hover:bg-primary-700 text-white rounded-md transition-colors text-sm font-medium"
                            title="Merge creatives"
                          >
                            <Copy className="h-4 w-4" />
                            Merge
                          </button>
                        </div>
                        
                        <div className="grid grid-cols-2 gap-4">
                          {/* First Creative */}
                          <div className="border border-gray-300 dark:border-gray-600 rounded p-3">
                            <div className="text-xs font-medium text-gray-700 dark:text-gray-300 mb-2">
                              {match.fileCreative.platform === 'direct_upload' || match.fileCreative.platform === 'dropbox' ? 'File Upload' : match.fileCreative.platform}
                            </div>
                            <div className="text-sm text-gray-900 dark:text-white mb-1">
                              {match.fileCreative.video_name || match.fileCreative.content?.split('/').pop() || 'Unnamed'}
                            </div>
                            <div className="text-xs text-gray-500 dark:text-gray-400 mb-2">
                              ID: {match.fileCreative.id}
                            </div>
                            {(match.fileCreative.thumbnail_url || match.fileCreative.instagram_thumbnail_url || match.fileCreative.tiktok_thumbnail_url) && (
                              <img
                                src={match.fileCreative.thumbnail_url || match.fileCreative.instagram_thumbnail_url || match.fileCreative.tiktok_thumbnail_url}
                                alt={`${match.fileCreative.platform} thumbnail`}
                                className="w-16 h-24 object-cover rounded"
                              />
                            )}
                          </div>
                          
                          {/* Second Creative */}
                          <div className="border border-gray-300 dark:border-gray-600 rounded p-3">
                            <div className="text-xs font-medium text-gray-700 dark:text-gray-300 mb-2">
                              {match.instagramCreative.platform === 'instagram' ? 'Instagram' : match.instagramCreative.platform}
                            </div>
                            <div className="text-sm text-gray-900 dark:text-white mb-1">
                              {match.instagramCreative.video_name || match.instagramCreative.content?.split('/').pop() || 'Unnamed'}
                            </div>
                            <div className="text-xs text-gray-500 dark:text-gray-400 mb-2">
                              ID: {match.instagramCreative.id}
                            </div>
                            {(match.instagramCreative.thumbnail_url || match.instagramCreative.instagram_thumbnail_url || match.instagramCreative.tiktok_thumbnail_url) && (
                              <img
                                src={match.instagramCreative.thumbnail_url || match.instagramCreative.instagram_thumbnail_url || match.instagramCreative.tiktok_thumbnail_url}
                                alt={`${match.instagramCreative.platform} thumbnail`}
                                className="w-16 h-24 object-cover rounded"
                              />
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}
            
            <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
              <button
                onClick={() => setShowDuplicateModal(false)}
                className="w-full px-4 py-2 bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600 text-gray-900 dark:text-white rounded-md transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Re-add Confirmation Modal */}
      {readdConfirmation.isOpen && readdConfirmation.creative && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg max-w-md w-full p-6">
            <h3 className="text-lg font-semibold dark:text-white mb-4">Re-add Creative to Campaign?</h3>
            <p className="text-gray-600 dark:text-gray-400 mb-2">
              This creative has already been added to Meta:
            </p>
            <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-3 mb-4 text-sm">
              <p className="dark:text-gray-300"><strong>Ad ID:</strong> {readdConfirmation.creative.meta_ad_id}</p>
              <p className="dark:text-gray-300"><strong>Status:</strong> {readdConfirmation.creative.meta_ad_status}</p>
            </div>
            <p className="text-gray-600 dark:text-gray-400 mb-4">
              Re-adding will create a <strong>new ad</strong> in the campaign. The existing ad will remain unchanged.
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setReaddConfirmation({ isOpen: false, creative: null })}
                className="px-4 py-2 text-gray-600 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-300"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  const creative = readdConfirmation.creative;
                  setReaddConfirmation({ isOpen: false, creative: null });
                  handleAddToCampaign(creative, true);
                }}
                className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg"
              >
                Re-add to Campaign
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Bulk Release Assignment Modal */}
      {showBulkReleaseModal && selectedArtistId && selectedCreatives.size > 0 && (
        <BulkAdCreativeReleaseModal
          creativeIds={Array.from(selectedCreatives)}
          creatives={filteredCreatives.filter(c => selectedCreatives.has(String(c.id)))}
          artistId={selectedArtistId}
          onClose={() => setShowBulkReleaseModal(false)}
          onSuccess={() => {
            setSelectedCreatives(new Set());
            const filters = {
              artistId: artistId || selectedArtistId || undefined,
              platform: selectedPlatform || undefined,
              status: selectedStatus || undefined
            };
            fetchAdCreatives(adCreativesPagination.currentPage, adCreativesPagination.pageSize, filters, true);
          }}
        />
      )}

      {/* Asks which campaign to add to before anything happens. It also
          reconciles against the live TikTok campaign while counting, so the
          numbers shown exclude creatives that are already running. */}
      {bulkAddPlatformFor && (
        <BulkAddPlatformModal
          releaseId={bulkAddPlatformFor.releaseId}
          releaseName={bulkAddPlatformFor.releaseName}
          metaCampaignId={bulkAddPlatformFor.metaCampaignId}
          tiktokCampaignId={bulkAddPlatformFor.tiktokCampaignId}
          filters={{ platform: selectedPlatform, status: selectedStatus }}
          onClose={() => setBulkAddPlatformFor(null)}
          onAddMeta={() => {
            const target = bulkAddPlatformFor;
            setBulkAddPlatformFor(null);
            handleBulkAddToRelease(target.releaseId, []);
          }}
          onAddTikTok={() => {
            const target = bulkAddPlatformFor;
            setBulkAddPlatformFor(null);
            if (target.tiktokCampaignId) {
              handleBulkAddToTikTokCampaign(target.releaseId, target.tiktokCampaignId);
            }
          }}
        />
      )}

      {/* Platform-aware link/unlink modal opened by clicking a divider pill.
          Supports Meta OR TikTok via `platform` prop; the modal itself
          handles search + unlink wiring. We only need to write back the
          selected campaign_id/name into the releases row here. */}
      {linkingCampaign && (
        <LinkReleaseCampaignModal
          platform={linkingCampaign.platform}
          releaseId={linkingCampaign.releaseId}
          releaseName={linkingCampaign.releaseName}
          currentCampaignId={linkingCampaign.currentCampaignId}
          currentCampaignName={linkingCampaign.currentCampaignName}
          onClose={() => setLinkingCampaign(null)}
          onSave={async (campaignId: string, campaignName: string) => {
            const releaseId = linkingCampaign.releaseId;
            if (!releaseId) {
              toast.error('Cannot link a campaign: these creatives have no release');
              return;
            }
            // Guard against an undefined id: JSON.stringify drops undefined
            // keys, so the PATCH body would be `{}` and PostgREST answers
            // 200 with an empty array — an update that silently does nothing.
            if (!campaignId) {
              toast.error('Cannot link campaign: missing campaign id');
              return;
            }
            const updatePayload =
              linkingCampaign.platform === 'meta'
                ? { meta_campaign_id: String(campaignId), meta_campaign_name: campaignName ?? '' }
                : { tiktok_campaign_id: String(campaignId), tiktok_campaign_name: campaignName ?? '' };
            console.log('[link-campaign] updating releases row', {
              releaseId,
              platform: linkingCampaign.platform,
              payload: updatePayload,
            });
            // .select() forces PostgREST to return the updated rows so we
            // can detect when RLS silently matches zero rows (which is what
            // "not saving" usually indicates).
            const { data, error } = await supabase
              .from('releases')
              .update(updatePayload as any)
              .eq('id' as any, releaseId as any)
              .select();
            if (error) {
              console.error('[link-campaign] update failed', error);
              toast.error(`Failed to link campaign: ${error.message}`);
              return;
            }
            if (!data || data.length === 0) {
              // Distinguish "row doesn't exist / not visible" from "update
              // was blocked": re-read the row with the same client.
              const probe = await supabase
                .from('releases')
                .select('id, tiktok_campaign_id, meta_campaign_id')
                .eq('id' as any, releaseId as any)
                .maybeSingle();
              const { data: sessionData } = await supabase.auth.getSession();
              console.error('[link-campaign] update matched 0 rows', {
                releaseId,
                supabaseUrl: import.meta.env.VITE_SUPABASE_URL,
                probeData: probe.data,
                probeError: probe.error,
                userId: sessionData.session?.user?.id ?? null,
                tokenRole: sessionData.session?.user?.role ?? null,
                tokenExpiresAt: sessionData.session?.expires_at
                  ? new Date(sessionData.session.expires_at * 1000).toISOString()
                  : null,
              });
              toast.error(
                probe.data
                  ? 'Campaign not linked: update blocked by permissions (RLS)'
                  : `Campaign not linked: release ${releaseId} not found`,
              );
              return;
            }
            console.log('[link-campaign] update ok', data);
            const filters = {
              artistId: artistId || selectedArtistId || undefined,
              platform: selectedPlatform || undefined,
              status: selectedStatus || undefined,
            };
            fetchAdCreatives(
              adCreativesPagination.currentPage,
              adCreativesPagination.pageSize,
              filters,
              true,
            );
            setLinkingCampaign(null);
            toast.success(
              `${linkingCampaign.platform === 'meta' ? 'Meta' : 'TikTok'} campaign linked`,
            );
          }}
        />
      )}

      {/* Smartlink (song.so) link modal — opened from divider's
          black "song.so" pill. Auto-searches by artist+release and
          allows manual search/select/unlink. */}
      {linkingSmartlink && (
        <LinkReleaseSmartlinkModal
          releaseId={linkingSmartlink.releaseId}
          releaseName={linkingSmartlink.releaseName}
          artistName={linkingSmartlink.artistName}
          currentSmartlinkUrl={linkingSmartlink.currentSmartlinkUrl}
          currentSmartlinkId={linkingSmartlink.currentSmartlinkId}
          onClose={() => setLinkingSmartlink(null)}
          onSaved={async () => {
            // Wipe ALL ad_creatives cache entries (any page/filter combo
            // could be stale now) and await the refetch so the divider
            // updates BEFORE the modal closes.
            cache.clearAll();
            const filters = {
              artistId: urlParams.artist || artistId || selectedArtistId || undefined,
              platform: selectedPlatform || undefined,
              status: selectedStatus || undefined,
            };
            await fetchAdCreatives(
              adCreativesPagination.currentPage,
              adCreativesPagination.pageSize,
              filters,
              true,
            );
          }}
        />
      )}

      {/* Report link modal — opened from divider's grey "Report" pill.
          Lists reports from the reporting DB; saves the report_id into
          release_campaign_links. */}
      {linkingReport && (
        <LinkReleaseReportModal
          releaseId={linkingReport.releaseId}
          releaseName={linkingReport.releaseName}
          artistName={linkingReport.artistName}
          artistId={linkingReport.artistId}
          currentReportId={linkingReport.currentReportId}
          onClose={() => setLinkingReport(null)}
          onSaved={async () => {
            cache.clearAll();
            const filters = {
              artistId: urlParams.artist || artistId || selectedArtistId || undefined,
              platform: selectedPlatform || undefined,
              status: selectedStatus || undefined,
            };
            await fetchAdCreatives(
              adCreativesPagination.currentPage,
              adCreativesPagination.pageSize,
              filters,
              true,
            );
          }}
        />
      )}

      {/* song.so confirmation dialog — replaces the native window.prompt
          when add-creative-to-meta-campaign returns 409. */}
      {confirmSmartlink && (
        <ConfirmSmartlinkModal
          suggestedUrl={confirmSmartlink.suggestedUrl}
          artistName={confirmSmartlink.artistName}
          releaseName={confirmSmartlink.releaseName}
          message={confirmSmartlink.message}
          onConfirm={(url) => confirmSmartlink.resolve(url)}
          onCancel={() => confirmSmartlink.resolve(null)}
        />
      )}

      {/* Legacy Meta-only edit-campaign modal (kept for backward compat with
          any remaining callers; divider pills now use LinkReleaseCampaignModal). */}
      {editingCampaignRelease && (
        <EditCampaignReleaseModal
          releaseId={editingCampaignRelease.releaseId}
          releaseName={editingCampaignRelease.releaseName}
          currentCampaignId={editingCampaignRelease.metaCampaignId}
          currentCampaignName={editingCampaignRelease.metaCampaignName}
          onClose={() => setEditingCampaignRelease(null)}
          onSave={async (campaignId: string, campaignName: string) => {
            // Update release with new campaign
            await supabase
              .from('releases')
              .update({ 
                meta_campaign_id: campaignId,
                meta_campaign_name: campaignName
              } as any)
              .eq('id' as any, editingCampaignRelease.releaseId as any);
            
            // Refresh ad creatives
            const filters = {
              artistId: artistId || selectedArtistId || undefined,
              platform: selectedPlatform || undefined,
              status: selectedStatus || undefined
            };
            fetchAdCreatives(adCreativesPagination.currentPage, adCreativesPagination.pageSize, filters, true);
            setEditingCampaignRelease(null);
            toast.success('Campaign updated');
          }}
        />
      )}

      {/* Bulk Thumbnail Generation Modal */}
      {showThumbnailModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              Generating Thumbnails
            </h3>
            
            {bulkThumbnailProgress && (
              <div className="space-y-4">
                <div className="text-sm text-gray-600 dark:text-gray-400">
                  Processing {bulkThumbnailProgress.processed} of {bulkThumbnailProgress.total} videos
                </div>
                
                <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2.5">
                  <div 
                    className="bg-purple-600 h-2.5 rounded-full transition-all duration-300"
                    style={{ width: `${(bulkThumbnailProgress.processed / bulkThumbnailProgress.total) * 100}%` }}
                  />
                </div>
                
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div className="text-green-600 dark:text-green-400">
                    ✓ Succeeded: {bulkThumbnailProgress.succeeded}
                  </div>
                  <div className="text-red-600 dark:text-red-400">
                    ✗ Failed: {bulkThumbnailProgress.failed}
                  </div>
                </div>
                
                {bulkThumbnailProgress.currentVideo && (
                  <div className="text-xs text-gray-500 dark:text-gray-400 truncate">
                    Current: {bulkThumbnailProgress.currentVideo}
                  </div>
                )}
              </div>
            )}
            
            {bulkThumbnailAbortController && (
              <div className="mt-4 flex justify-center">
                <button
                  onClick={() => bulkThumbnailAbortController.abort()}
                  className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-md transition-colors"
                >
                  Cancel
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {assetRelease && (
        <ReleaseAssetsModal
          release={assetRelease}
          onClose={() => setAssetRelease(null)}
          onSaved={(patch) => setAssetRelease((prev: any) => prev ? { ...prev, ...patch } : prev)}
        />
      )}

      {/* Auto-link running indicator */}
      {autoLinkRunning && (
        <div className="fixed bottom-4 right-4 bg-white dark:bg-gray-800 rounded-lg shadow-lg p-3 flex items-center gap-2 z-50">
          <Loader className="h-4 w-4 animate-spin text-purple-600" />
          <span className="text-sm text-gray-700 dark:text-gray-300">
            Auto-linking releases...
          </span>
        </div>
      )}

    </div>
  );
}